// ChatBox.js - With enhanced duplicate detection
import React, { useState, useEffect, useRef, createContext, useContext } from "react";
import stringSimilarity from "string-similarity";
import QuizQuestion from "./QuizQuestion.js";
import QuestionHistory from "./QuestionHistory.js";
import QuizDiagnostics from "./QuizDiagnostics.js"; // Optional - import the diagnostic component
import StatsDebugger from "./debug/StatsDebugger.js"; // Import for debugging
import { setupVisibilityDetection } from "./components/ChatBox/utils/visibilityUtils.js"; // Import visibility utility
import "./ChatBox.css";

// Create ChatContext for sharing state between components
export const ChatContext = createContext(null);

function renderStaticQuiz(quizData, chosen) {
  if (!quizData) return "Quiz data not available.";
  
  const { question, choices, correctAnswer, difficulty } = quizData;
  
  // Define icon for each difficulty level
  const difficultyIcons = {
    easy: "⭐",
    medium: "⭐⭐",
    hard: "⭐⭐⭐"
  };
  
  // Display difficulty if available
  const difficultyDisplay = difficulty ? 
    `<div class="quiz-difficulty ${difficulty}">${difficultyIcons[difficulty] || ''} ${difficulty.toUpperCase()}</div>` : '';
  
  // Build the HTML with proper highlighting
  const result = `<div class="static-quiz-container">
    <h3>${question}</h3>
    ${difficultyDisplay}
    <div class="static-quiz-options">
      ${choices.map(choice => {
        let className = "";
        if (correctAnswer === choice.label) {
          className += " correct";
        }
        if (chosen === choice.label && correctAnswer !== choice.label) {
          className += " incorrect";
        }
        return `<div class="static-quiz-option${className}">
          <span class="option-label">${choice.label}</span> ${choice.text}
        </div>`;
      }).join('')}
    </div>
  </div>`;
  
  return result;
}

function parseQuiz(text) {
  // Safety check for empty or undefined input
  if (!text) {
    console.error("Empty text passed to parseQuiz");
    return null;
  }

  console.log("Parsing quiz text:", text.substring(0, 100) + "...");

  // Check for debugging output mixed in with the response
  if (text.includes("API Endpoint") || text.includes("Request method:")) {
    console.warn("Found debugging output in quiz text, attempting to extract clean quiz");

    // Find the last occurrence of "Question:" which is likely the complete version
    const lastQuestionIndex = text.lastIndexOf("Question:");
    if (lastQuestionIndex >= 0) {
      text = text.substring(lastQuestionIndex);
      console.log("Extracted quiz text:", text.substring(0, 100) + "...");
    }
  }

  // Check for minimum quiz requirements
  if (!text.includes("Question:") ||
      !text.includes("A)") ||
      !text.includes("Correct Answer:")) {
    console.error("Text does not contain required quiz elements");
    return null;
  }

  const lines = text
    .split("\n")
    .map(line => line.trim())
    .filter(line => line && !line.startsWith("Meme:"));

  // Extract explanation if it exists
  let explanationText = "";
  let explanationStartIndex = -1;
  
  // Add variables for wrong answer explanations
  let wrongAnswerExplanations = {
    A: "",
    B: "",
    C: "",
    D: ""
  };

  for (let i = 0; i < lines.length; i++) {
    if (lines[i].startsWith("Explanation:")) {
      explanationStartIndex = i;
      break;
    }
  }

  if (explanationStartIndex !== -1) {
    // Collect all lines of the explanation (could span multiple lines)
    const explanationLines = [];
    for (let i = explanationStartIndex; i < lines.length; i++) {
      // Stop if we hit another section or a wrong answer explanation
      if (i > explanationStartIndex && 
          (lines[i].startsWith("Meme:") || 
           lines[i].startsWith("Question:") ||
           lines[i].startsWith("A is wrong because:") ||
           lines[i].startsWith("B is wrong because:") ||
           lines[i].startsWith("C is wrong because:") ||
           lines[i].startsWith("D is wrong because:"))) break;
      explanationLines.push(lines[i]);
    }
    
    // Join and format the explanation, preserving line breaks for better readability
    explanationText = explanationLines.join("\n")
      .replace("Explanation:", "")
      .trim();
      
    // Log the full explanation for debugging
    console.log("Extracted explanation:", explanationText);
  }
  
  // Extract wrong answer explanations
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].startsWith("A is wrong because:")) {
      wrongAnswerExplanations.A = lines[i].replace("A is wrong because:", "").trim();
    } else if (lines[i].startsWith("B is wrong because:")) {
      wrongAnswerExplanations.B = lines[i].replace("B is wrong because:", "").trim();
    } else if (lines[i].startsWith("C is wrong because:")) {
      wrongAnswerExplanations.C = lines[i].replace("C is wrong because:", "").trim();
    } else if (lines[i].startsWith("D is wrong because:")) {
      wrongAnswerExplanations.D = lines[i].replace("D is wrong because:", "").trim();
    }
  }
  
  console.log("Extracted wrong answer explanations:", wrongAnswerExplanations);

  const quizLines = [];
  for (let line of lines) {
    if (line.startsWith("Explanation:") || 
        line.startsWith("A is wrong because:") || 
        line.startsWith("B is wrong because:") || 
        line.startsWith("C is wrong because:") || 
        line.startsWith("D is wrong because:")) break;
    quizLines.push(line);
  }

  const quizData = { 
    question: "", 
    choices: [], 
    correctAnswer: "",
    explanation: explanationText, // Add the explanation to the quiz data
    wrongAnswerExplanations: wrongAnswerExplanations // Add wrong answer explanations
  };

  // Extract question
  const questionLine = quizLines.find(line => line.startsWith("Question:"));
  if (questionLine) {
    quizData.question = questionLine.replace("Question:", "").trim();
  }

  // Extract choices - looking for various formats
  const choicePatterns = [
    /^([A-D])\)\s*(.+)$/,        // A) Choice text
    /^([A-D])\.\s*(.+)$/,        // A. Choice text
    /^\(([A-D])\)\s*(.+)$/,      // (A) Choice text
    /^([A-D])\s*-\s*(.+)$/,      // A - Choice text
    /^Option\s*([A-D]):\s*(.+)$/  // Option A: Choice text
  ];

  for (let line of quizLines) {
    let matched = false;

    for (let pattern of choicePatterns) {
      const match = line.match(pattern);
      if (match) {
        quizData.choices.push({
          label: match[1],
          text: match[2].trim()
        });
        matched = true;
        break;
      }
    }

    // Check for the correct answer
    if (line.startsWith("Correct Answer:")) {
      quizData.correctAnswer = line.replace("Correct Answer:", "").trim();
      // Sometimes the answer has extra punctuation
      quizData.correctAnswer = quizData.correctAnswer.replace(/[^A-D]/g, "");
    }
  }

  // Ensure we have the basics
  if (!quizData.question || !quizData.correctAnswer) {
    console.error("Missing basic quiz elements:", quizData);
    return null;
  }

  // Log what we've found
  console.log("Found quiz question:", quizData.question);
  console.log("Found choices:", quizData.choices.length);
  console.log("Found correct answer:", quizData.correctAnswer);

  // Check for the most common error: not all choices found
  const expectedLabels = ["A", "B", "C", "D"];
  const actualLabels = quizData.choices.map(c => c.label);

  if (quizData.choices.length < 4) {
    console.error("Not enough choices found:", actualLabels);

    // Try to scan the whole text again for any missed choices
    for (const label of expectedLabels) {
      if (!actualLabels.includes(label)) {
        const pattern = new RegExp(`${label}\\)\\s*(.+)`, "i");
        const match = text.match(pattern);
        if (match) {
          console.log(`Found missing choice ${label} in full text:`, match[1]);
          quizData.choices.push({
            label: label,
            text: match[1].trim()
          });
        }
      }
    }
  }

  // Final validation
  const updatedLabels = quizData.choices.map(c => c.label);
  const isValid =
    quizData.question &&
    quizData.choices.length === 4 &&
    expectedLabels.every(label => updatedLabels.includes(label)) &&
    quizData.correctAnswer &&
    expectedLabels.includes(quizData.correctAnswer);

  if (!isValid) {
    console.error("Invalid quiz data:", quizData);
    console.log("Found choices:", updatedLabels.join(", "));
    return null;
  }

  console.log("Successfully parsed valid quiz");
  return quizData;
}

// Loading indicator component
const LoadingIndicator = () => (
  <div className="loading-container">
    <div className="loading-dots">
      <div className="dot"></div>
      <div className="dot"></div>
      <div className="dot"></div>
    </div>
    <div className="loading-text">Loading...</div>
  </div>
);

// Timer options component
const TimerOptions = ({ selectedTimer, onSelectTimer }) => {
  const options = [
    { value: 10, label: "10s" },
    { value: 15, label: "15s" },
    { value: 30, label: "30s" },
    { value: 60, label: "60s" }
  ];

  return (
    <div className="timer-options">
      <span className="timer-label">Timer:</span>
      <div className="timer-buttons">
        {options.map(option => (
          <button
            key={option.value}
            className={`timer-button ${selectedTimer === option.value ? 'selected' : ''}`}
            onClick={() => onSelectTimer(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
};

// Debug display component for development
const DebugMessage = ({ text }) => (
  <div className="debug-message">
    {text}
  </div>
);

// User Stats component to display stats without admin dashboard
const UserStats = ({ onClose }) => {
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Define rizz thresholds for topic-specific levels - moved outside of processTopics
  const levelThresholds = [
    { level: 1, title: 'Apprentice', rizz: 250 },   // Was 100
    { level: 2, title: 'Adept', rizz: 750 },        // Was 250
    { level: 3, title: 'Specialist', rizz: 1500 },  // Was 500
    { level: 4, title: 'Expert', rizz: 3000 },      // Was 1000
    { level: 5, title: 'Master', rizz: 6000 },      // Was 2000
    { level: 6, title: 'Grandmaster', rizz: 10000 }, // New level
    { level: 7, title: 'Legendary', rizz: 15000 }    // New level
  ];

  useEffect(() => {
    // Load user stats from localStorage
    try {
      const rawStats = localStorage.getItem('userStats');
      if (rawStats) {
        const parsedStats = JSON.parse(rawStats);
        
        // Also get the current streak and max streak from localStorage
        const streakData = JSON.parse(localStorage.getItem('techGuyGregStreak') || '{"current":0,"max":0}');
        
        // Merge the streak data with the stats data
        setStatsData({
          ...parsedStats,
          currentStreak: streakData.current || 0,
          maxStreak: streakData.max || parsedStats.maxStreak || 0
        });
      } else {
        // Initialize with empty stats if none exist
        setStatsData({
          totalQuestions: 0,
          correctAnswers: 0,
          incorrectAnswers: 0,
          skippedQuestions: 0,
          currentStreak: 0,
          maxStreak: 0,
          totalRizzPoints: 0,
          topicsDistribution: {},
          dailyActivity: {},
          difficultyStats: {
            easy: { count: 0, points: 0 },
            medium: { count: 0, points: 0 },
            hard: { count: 0, points: 0 }
          },
          averageResponseTime: 0,
          totalResponseTime: 0,
          // Add tracking of processed question IDs
          processedQuestionIds: []
        });
      }
    } catch (error) {
      console.error('Error loading user stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Function to get last 7 days for activity chart
  const getLastSevenDays = () => {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      days.push(date.toISOString().split('T')[0]);
    }
    return days;
  };

  // Get day label (e.g., "Mon", "Tue")
  const getDayLabel = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  // Process topics for visualization with enhanced metrics
  const processTopics = () => {
    if (!statsData || !statsData.topicsDistribution) return [];
    
    // Get all topics with their counts
    const topics = Object.entries(statsData.topicsDistribution || {}).map(([name, count]) => ({ name, count }));
    
    // Sort by count (most frequent first)
    topics.sort((a, b) => b.count - a.count);
    
    // Get total questions for percentage calculation
    const total = topics.reduce((sum, topic) => sum + topic.count, 0);
    
    // Get daily activity to calculate recent activity
    const dailyActivity = statsData.dailyActivity || {};
    const last7Days = getLastSevenDays();
    
    // Get topic-specific rizz points and levels
    const topicRizz = statsData.topicRizz || {};
    
    // Calculate topic-specific metrics
    return topics.slice(0, 5).map(topic => {
      // Calculate percentage of total questions
      const percentage = total > 0 ? (topic.count / total) * 100 : 0;
      
      // Calculate recent activity (questions in this topic in the last 3 days)
      let recentCount = 0;
      const recentDays = last7Days.slice(4); // Last 3 days
      
      // This is a simplification - in a real app, you'd track topic per question in daily activity
      // For now, we'll estimate based on the topic's overall percentage
      recentDays.forEach(day => {
        if (dailyActivity[day]) {
          const dayTotal = (dailyActivity[day].correct || 0) + (dailyActivity[day].incorrect || 0);
          // Estimate this topic's contribution based on its overall percentage
          recentCount += Math.round(dayTotal * (percentage / 100));
        }
      });
      
      // Get topic-specific rizz points
      const topicRizzPoints = topicRizz[topic.name] || 0;
      
      // Calculate topic-specific mastery level
      let level = 0;
      let mastery = 'Novice';
      
      // Find the highest level threshold that the topic's rizz points exceed
      for (let i = levelThresholds.length - 1; i >= 0; i--) {
        if (topicRizzPoints >= levelThresholds[i].rizz) {
          level = levelThresholds[i].level;
          mastery = levelThresholds[i].title;
          break;
        }
      }
      
      // Calculate growth (trending up if recent questions represent a significant portion)
      const growth = topic.count > 0 ? (recentCount / topic.count) : 0;
      const trending = growth > 0.2; // Trending if >20% of questions were recent
      
      return {
        ...topic,
        percentage,
        mastery,
        level,
        rizzPoints: topicRizzPoints,
        recentCount,
        trending
      };
    });
  };

  // Calculate average points by difficulty
  const getDifficultyStats = () => {
    const difficultyStats = statsData?.difficultyStats || {
      easy: { count: 0, points: 0 },
      medium: { count: 0, points: 0 },
      hard: { count: 0, points: 0 }
    };
    
    // Calculate average for each difficulty
    return Object.entries(difficultyStats).map(([difficulty, data]) => ({
      difficulty,
      count: data.count || 0,
      totalPoints: data.points || 0,
      avgPoints: data.count > 0 ? Math.round(data.points / data.count) : 0
    }));
  };
  
  // Calculate learning progress metrics
  const getLearningProgress = () => {
    if (!statsData) return { 
      topicsLearned: 0, 
      masteryLevel: 'Novice', 
      numericLevel: 0,
      nextLevel: 0, 
      currentRizz: 0, 
      rizzToNextLevel: 500,
      adjustedRizz: 0
    };
    
    // Count topics with at least 3 questions answered
    const topicsLearned = Object.entries(statsData.topicsDistribution || {})
      .filter(([_, count]) => count >= 3).length;
    
    // Get total rizz points
    const totalRizzPoints = statsData.totalRizzPoints || 0;
    
    // Define title ranks with their level ranges - now with higher requirements
    const titleRanks = [
      { title: 'Novice', minLevel: 0, maxLevel: 0, rizzRequired: 0 },
      { title: 'Apprentice', minLevel: 1, maxLevel: 10, rizzRequired: 500 },  // Start at 500 (was 100)
      { title: 'Adept', minLevel: 11, maxLevel: 20, rizzRequired: 2000 },     // Was 1000
      { title: 'Specialist', minLevel: 21, maxLevel: 30, rizzRequired: 5000 }, // Was 2000
      { title: 'Expert', minLevel: 31, maxLevel: 40, rizzRequired: 10000 },    // Was 3500
      { title: 'Master', minLevel: 41, maxLevel: 50, rizzRequired: 20000 },    // Was 5500
      { title: 'Grandmaster', minLevel: 51, maxLevel: 60, rizzRequired: 35000 }, // Was 8000
      { title: 'Legendary', minLevel: 61, maxLevel: 70, rizzRequired: 55000 },   // Was 11000
      { title: 'Mythic', minLevel: 71, maxLevel: 80, rizzRequired: 80000 },      // Was 15000
      { title: 'Divine', minLevel: 81, maxLevel: 100, rizzRequired: 120000 }     // Was 20000
    ];
    
    // Calculate level requirements with steeper progression
    const levelRequirements = [];
    let previousLevelRizz = 0;
    
    titleRanks.forEach(rank => {
      if (rank.minLevel === 0) {
        levelRequirements.push({
          level: 0,
          title: rank.title,
          rizzRequired: 0,
          rizzForNextLevel: 500 // First level requires 500 points
        });
        previousLevelRizz = 0;
        return;
      }
      
      let levelRizz = rank.rizzRequired;
      const levelsInRank = rank.maxLevel - rank.minLevel + 1;
      
      for (let i = 0; i < levelsInRank; i++) {
        const level = rank.minLevel + i;
        // Steeper progression within ranks - each level gets 20% harder (was 15%)
        const levelMultiplier = 1 + (i * 0.2); 
        const rizzForThisLevel = Math.round(levelRizz * levelMultiplier / levelsInRank);
        
        previousLevelRizz += (i === 0 && level > 1) ? 0 : rizzForThisLevel;
        
        // Steeper progression between levels
        const nextLevelRizz = (i === levelsInRank - 1 && rank.title === 'Divine') 
          ? rizzForThisLevel * 2.0 // For the final level (100) - was 1.5
          : rizzForThisLevel * 1.25; // Normal progression - was 1.15
        
        levelRequirements.push({
          level,
          title: rank.title,
          rizzRequired: previousLevelRizz,
          rizzForNextLevel: Math.round(nextLevelRizz)
        });
      }
    });
    
    // Find current level based on total rizz points
    let currentLevel = 0;
    let currentTitle = 'Novice';
    let rizzToNextLevel = 500;
    let adjustedRizz = totalRizzPoints;
    
    for (let i = 1; i < levelRequirements.length; i++) {
      if (totalRizzPoints >= levelRequirements[i].rizzRequired) {
        currentLevel = levelRequirements[i].level;
        currentTitle = levelRequirements[i].title;
        adjustedRizz = totalRizzPoints - levelRequirements[i].rizzRequired;
        
        // Calculate rizz needed for next level
        if (i < levelRequirements.length - 1) {
          rizzToNextLevel = levelRequirements[i + 1].rizzRequired - levelRequirements[i].rizzRequired;
        } else {
          // For max level
          rizzToNextLevel = levelRequirements[i].rizzForNextLevel;
        }
      } else {
        // We've found the next level the user hasn't reached yet
        rizzToNextLevel = levelRequirements[i].rizzRequired - totalRizzPoints;
        break;
      }
    }
    
    // Calculate progress percentage to next level
    const progress = Math.min(100, Math.round((adjustedRizz / rizzToNextLevel) * 100));
    
    return { 
      topicsLearned, 
      masteryLevel: currentTitle,
      numericLevel: currentLevel,
      nextLevel: progress,
      currentRizz: totalRizzPoints,
      rizzToNextLevel: rizzToNextLevel,
      adjustedRizz: adjustedRizz
    };
  };
  
  // Calculate performance trends
  const getPerformanceTrends = () => {
    if (!statsData || !statsData.dailyActivity) return { improving: false, trend: 0 };
    
    const last7Days = getLastSevenDays();
    const dailyActivity = statsData.dailyActivity || {};
    
    // Calculate accuracy for first half and second half of the week
    const firstHalf = last7Days.slice(0, 3);
    const secondHalf = last7Days.slice(3);
    
    let firstHalfCorrect = 0, firstHalfTotal = 0;
    let secondHalfCorrect = 0, secondHalfTotal = 0;
    
    firstHalf.forEach(day => {
      if (dailyActivity[day]) {
        firstHalfCorrect += dailyActivity[day].correct || 0;
        firstHalfTotal += (dailyActivity[day].correct || 0) + (dailyActivity[day].incorrect || 0);
      }
    });
    
    secondHalf.forEach(day => {
      if (dailyActivity[day]) {
        secondHalfCorrect += dailyActivity[day].correct || 0;
        secondHalfTotal += (dailyActivity[day].correct || 0) + (dailyActivity[day].incorrect || 0);
      }
    });
    
    const firstHalfAccuracy = firstHalfTotal > 0 ? firstHalfCorrect / firstHalfTotal : 0;
    const secondHalfAccuracy = secondHalfTotal > 0 ? secondHalfCorrect / secondHalfTotal : 0;
    
    const improving = secondHalfAccuracy > firstHalfAccuracy;
    const trend = secondHalfTotal > 0 && firstHalfTotal > 0 
      ? Math.round((secondHalfAccuracy - firstHalfAccuracy) * 100) 
      : 0;
    
    return { improving, trend };
  };

  if (loading) {
    return (
      <div className="stats-modal">
        <div className="stats-content">
          <div className="stats-header">
            <h2>Your Stats</h2>
            <button onClick={onClose} aria-label="Close stats" title="Close stats"></button>
          </div>
          <div className="stats-body" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
            Loading your stats...
          </div>
        </div>
      </div>
    );
  }

  // Calculate accuracy percentage
  const totalAnswered = statsData.correctAnswers + statsData.incorrectAnswers;
  const accuracyPercentage = totalAnswered > 0 
    ? Math.round((statsData.correctAnswers / totalAnswered) * 100) 
    : 0;

  // Get average response time in seconds, rounded to 1 decimal place
  const avgResponseTime = statsData.averageResponseTime 
    ? Math.round(statsData.averageResponseTime * 10) / 10 
    : 0;

  // Get last 7 days of activity
  const last7Days = getLastSevenDays();
  const dailyActivity = statsData.dailyActivity || {};

  // Get difficulty statistics
  const difficultyStats = getDifficultyStats();
  
  // Get learning progress
  const learningProgress = getLearningProgress();
  
  // Get performance trends
  const performanceTrends = getPerformanceTrends();

  // Fix for potentially glitched stats - ensure we have valid numbers
  const safeStats = {
    totalQuestions: Number.isInteger(statsData.totalQuestions) ? statsData.totalQuestions : 0,
    correctAnswers: Number.isInteger(statsData.correctAnswers) ? statsData.correctAnswers : 0,
    incorrectAnswers: Number.isInteger(statsData.incorrectAnswers) ? statsData.incorrectAnswers : 0,
    currentStreak: Number.isInteger(statsData.currentStreak) ? statsData.currentStreak : 0,
    maxStreak: Number.isInteger(statsData.maxStreak) ? statsData.maxStreak : 0
  };

  return (
    <div className="stats-modal">
      <div className="stats-content">
        <div className="stats-header">
          <h2>Your Stats</h2>
          <button onClick={onClose} aria-label="Close stats" title="Close stats"></button>
        </div>
        <div className="stats-body">
          {/* Ensure all stats are safely initialized to prevent NaN displays */}
          {(() => {
            // Make sure all required stats have default values
            const safeStats = {
              totalQuestions: Number.isInteger(statsData?.totalQuestions) ? statsData.totalQuestions : 0,
              correctAnswers: Number.isInteger(statsData?.correctAnswers) ? statsData.correctAnswers : 0,
              incorrectAnswers: Number.isInteger(statsData?.incorrectAnswers) ? statsData.incorrectAnswers : 0,
              skippedQuestions: Number.isInteger(statsData?.skippedQuestions) ? statsData.skippedQuestions : 0,
              currentStreak: Number.isInteger(statsData?.currentStreak) ? statsData.currentStreak : 0,
              maxStreak: Number.isInteger(statsData?.maxStreak) ? statsData.maxStreak : 0
            };
            
            // Calculate accuracy percentage
            const totalAnswered = safeStats.correctAnswers + safeStats.incorrectAnswers;
            const accuracyPercentage = totalAnswered > 0 
              ? Math.round((safeStats.correctAnswers / totalAnswered) * 100) 
              : 0;
              
            // Calculate average response time
            const avgResponseTime = statsData?.averageResponseTime 
              ? Math.round(statsData.averageResponseTime) 
              : 0;
              
            return (
              <>
          <div className="stats-summary">
            <div className="stat-item">
              <div className="stat-label">Total Questions</div>
              <div className="stat-value">{safeStats.totalQuestions.toLocaleString()}</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Correct</div>
                    <div className="stat-value correct-value">{safeStats.correctAnswers.toLocaleString()}</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-label">Wrong</div>
                    <div className="stat-value wrong-value">{safeStats.incorrectAnswers.toLocaleString()}</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Accuracy</div>
              <div className="stat-value">{accuracyPercentage}%</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Best Streak</div>
              <div className="stat-value">{safeStats.maxStreak}</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Avg. Time</div>
              <div className="stat-value">{avgResponseTime}s</div>
            </div>
          </div>
              </>
            );
          })()}
          
          {/* Learning Progress */}
          <div className="stats-section">
            <h3>Learning Progress</h3>
            <div className="learning-progress">
              <div className="mastery-level">
                <div className="level-info">
                  <div className="level-value">
                    <span className="level-number" data-level={Math.floor(getLearningProgress().numericLevel / 10) * 10 + 1 + "-" + (Math.floor(getLearningProgress().numericLevel / 10) + 1) * 10}>
                      Lv. {getLearningProgress().numericLevel}
                    </span>
                    <span className="level-title" data-rank={getLearningProgress().masteryLevel}>
                      {getLearningProgress().masteryLevel}
                    </span>
                  </div>
                  <div className="level-stats">
                    <div className="level-stat">
                      <span className="stat-icon">📚</span>
                      <span className="stat-value">{getLearningProgress().topicsLearned}</span>
                      <span className="stat-label">Topics</span>
                    </div>
                    <div className="level-stat">
                      <span className="stat-icon">⭐</span>
                      <span className="stat-value">{getLearningProgress().currentRizz.toLocaleString()}</span>
                      <span className="stat-label">Rizz</span>
                    </div>
                    <div className="level-stat">
                      <span className="stat-icon">🔼</span>
                      <span className="stat-value">{getLearningProgress().rizzToNextLevel.toLocaleString()}</span>
                      <span className="stat-label">To Next</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="progress-bar-container">
                <div 
                  className="progress-bar" 
                  style={{ width: `${getLearningProgress().nextLevel}%` }}
                ></div>
                <div className="progress-percentage">{getLearningProgress().nextLevel}%</div>
              </div>
            </div>
          </div>
          
          {/* Topics Breakdown - Moved up */}
          <div className="stats-section">
            <h3>Topics Breakdown</h3>
            {processTopics().length > 0 ? (
              <div className="topic-breakdown">
                {processTopics().map((topic, index) => (
                  <div key={index} className="topic-item">
                    <div className="topic-header">
                      <div className="topic-name">{topic.name}</div>
                      <div className="topic-mastery" data-level={topic.mastery.toLowerCase()}>
                        <span className="topic-level-badge" data-level={topic.level}>Lv.{topic.level}</span>
                        <span className="topic-mastery-title">{topic.mastery}</span>
                      </div>
                    </div>
                    
                    <div className="topic-details">
                      <div className="topic-bar-container">
                        <div 
                          className="topic-bar" 
                          style={{ 
                            width: `${Math.min(100, topic.percentage)}%`,
                            background: `linear-gradient(90deg, 
                              hsl(${index * 30}, 85%, 65%), 
                              hsl(${index * 30 + 15}, 85%, 55%))`
                          }}
                        >
                          {topic.percentage >= 15 && (
                            <span className="topic-bar-label">
                              {Math.round(topic.percentage)}%
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="topic-metrics">
                        <div className="topic-count">
                          <span className="count-value">{topic.count}</span>
                          <span className="count-label">Q</span>
                        </div>
                        
                        <div className="topic-rizz">
                          <span className="rizz-value">{topic.rizzPoints.toLocaleString()}</span>
                          <span className="rizz-label">Rizz</span>
                        </div>
                        
                        {topic.trending && (
                          <div className="topic-trending">
                            <span className="trending-icon">↑</span>
                          </div>
                        )}
                        
                        {topic.recentCount > 0 && (
                          <div className="topic-recent">
                            <span className="recent-value">+{topic.recentCount}</span>
                          </div>
                        )}
                        
                        {topic.level < 7 && (
                          <div className="topic-next-level">
                            <span className="next-level-value">
                              {(levelThresholds[topic.level]?.rizz - topic.rizzPoints).toLocaleString()}
                            </span>
                            <span className="next-level-label">to Lv.{topic.level + 1}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-data">
                Start answering questions to see topic stats
              </div>
            )}
          </div>
          
          {/* Difficulty Breakdown - Moved down */}
          <div className="stats-section">
            <h3>Difficulty Breakdown</h3>
            <div className="difficulty-breakdown">
              {difficultyStats.map((diff, index) => (
                <div key={index} className={`difficulty-item ${diff.difficulty}`}>
                  <div className="difficulty-header">
                    <div className="difficulty-name">
                      {diff.difficulty === 'easy' && '⭐ Easy'}
                      {diff.difficulty === 'medium' && '⭐⭐ Medium'}
                      {diff.difficulty === 'hard' && '⭐⭐⭐ Hard'}
                    </div>
                    <div className="difficulty-count">{diff.count} questions</div>
                  </div>
                  <div className="difficulty-stats">
                    <div className="difficulty-points">
                      <span className="points-label">Total:</span>
                      <span className="points-value">{diff.totalPoints.toLocaleString()} rizz pts</span>
                    </div>
                    <div className="difficulty-avg">
                      <span className="avg-label">Avg:</span>
                      <span className="avg-value">{diff.avgPoints} pts/question</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Recent Activity */}
          <div className="stats-section">
            <h3>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="activity-icon">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
              </svg>
              Recent Activity
            </h3>
            <div className="activity-chart">
              {last7Days.map((day, index) => (
                <div key={index} className="activity-day">
                  <div className="day-bars">
                    {dailyActivity[day] ? (
                      <>
                        <div 
                          className="correct-bar"
                          style={{ height: `${Math.min(100, (dailyActivity[day].correct || 0) * 10)}px` }}
                          title={`${dailyActivity[day].correct || 0} correct answers`}
                        >
                          {dailyActivity[day].correct > 0 && <span className="bar-value">{dailyActivity[day].correct}</span>}
                        </div>
                        <div 
                          className="incorrect-bar"
                          style={{ height: `${Math.min(100, (dailyActivity[day].incorrect || 0) * 10)}px` }}
                          title={`${dailyActivity[day].incorrect || 0} incorrect answers`}
                        >
                          {dailyActivity[day].incorrect > 0 && <span className="bar-value">{dailyActivity[day].incorrect}</span>}
                        </div>
                      </>
                    ) : (
                      <div className="no-data-bar" title="No activity on this day"></div>
                    )}
                  </div>
                  <div className="day-label">{getDayLabel(day)}</div>
                </div>
              ))}
            </div>
            <div className="activity-legend">
              <div className="legend-item">
                <div className="legend-color correct"></div>
                <div>Correct</div>
              </div>
              <div className="legend-item">
                <div className="legend-color incorrect"></div>
                <div>Incorrect</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function ChatBox() {
  // State management
  const [messages, setMessages] = useState([]);
  const [askedQuestions, setAskedQuestions] = useState(() => {
    try {
      const savedQuestions = localStorage.getItem('techGuyGregAskedQuestions');
      if (!savedQuestions) return [];

      const parsed = JSON.parse(savedQuestions);
      if (!Array.isArray(parsed)) {
        console.error("Saved questions is not an array, resetting");
        return [];
      }

      // More robust duplicate removal with better question normalization
      const normalizeText = (text) => {
        if (!text || typeof text !== 'string') return '';
        return text
          .toLowerCase()
          .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()?"']/g, "")
          .replace(/\s+/g, " ")
          .trim();
      };

      // Use a Map for efficient duplicate checking
      const uniqueQuestionsMap = new Map();

      parsed.forEach(current => {
        // Skip invalid entries
        if (!current) return;

        // Extract the question text consistently
        const questionText = typeof current === 'string'
          ? current
          : (current.text || current.question || "");

        if (!questionText) return;

        const normalizedText = normalizeText(questionText);

        // Ensure boolean properties are correctly typed
        if (typeof current === 'object') {
          // Fix isCorrect and isSkipped properties
          current.isCorrect = current.isCorrect === true ? true : 
                             current.isCorrect === false ? false : null;
          current.isSkipped = current.isSkipped === true;
        }

        // Only keep the latest version of each question
        uniqueQuestionsMap.set(normalizedText, current);
      });

      // Convert back to array and log the count
      const uniqueQuestions = Array.from(uniqueQuestionsMap.values());
      console.log(`Loaded ${uniqueQuestions.length} unique questions from localStorage`);
      
      // Log a sample question to verify properties
      if (uniqueQuestions.length > 0) {
        const sample = uniqueQuestions[uniqueQuestions.length - 1];
        console.log("Sample question from localStorage:", {
          text: typeof sample === 'object' && sample.question ? 
                sample.question.substring(0, 30) + "..." : "No text",
          isCorrect: sample.isCorrect,
          isSkipped: sample.isSkipped
        });
      }
      
      return uniqueQuestions;
    } catch (error) {
      console.error("Error loading questions from localStorage:", error);
      return [];
    }
  });
  const [currentQuiz, setCurrentQuiz] = useState(null);
  const [input, setInput] = useState("");
  const [topic, setTopic] = useState("LeetCode");
  const [score, setScore] = useState({ correct: 0, wrong: 0 });
  const [streak, setStreak] = useState(() => {
    const savedStreak = localStorage.getItem('userStreak');
    return savedStreak ? JSON.parse(savedStreak) : { current: 0, max: 0 };
  });
  const [rizzPoints, setRizzPoints] = useState(() => {
    const savedRizzPoints = localStorage.getItem('userRizzPoints');
    return savedRizzPoints ? parseInt(savedRizzPoints, 10) : 0;
  });
  const [rizzAnimating, setRizzAnimating] = useState(false);
  const [rizzPointsChange, setRizzPointsChange] = useState(0);
  const [isAnswering, setIsAnswering] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [timerDuration, setTimerDuration] = useState(30);
  const [showHistory, setShowHistory] = useState(false);
  const [showStats, setShowStats] = useState(false); // Add state for showing stats
  const [isLoading, setIsLoading] = useState(false);
  const [debugMode, setDebugMode] = useState(false); // For development only
  const [historyUpdateFlag, setHistoryUpdateFlag] = useState(0); // Flag to force history updates
  const [topicCoverage, setTopicCoverage] = useState(() => {
    try {
      const savedCoverage = localStorage.getItem('techGuyGregTopicCoverage');
      return savedCoverage ? JSON.parse(savedCoverage) : {};
    } catch (error) {
      console.error("Error loading topic coverage:", error);
      return {};
    }
  });
  // Remove the old consecutiveSkippedQuestions state
  // const [consecutiveSkippedQuestions, setConsecutiveSkippedQuestions] = useState(0);
  
  // Add new state for counting consecutive questions
  // const [consecutiveQuestions, setConsecutiveQuestions] = useState(0);
  
  const [consecutiveQuestions, setConsecutiveQuestions] = useState(0);
  
  // Add new state for tracking consecutive unanswered questions
  const [consecutiveUnanswered, setConsecutiveUnanswered] = useState(0);
  
  const chatRef = useRef(null);
  // Ref to track the current quiz start time for calculating response times
  const quizStartTimeRef = useRef(null);

  // Ref to track if fetch is already in progress to prevent duplicate requests
  const fetchInProgressRef = useRef(false);
  
  // Ref to track if an answer is being processed to prevent duplicates
  const isAnsweringRef = useRef(false);
  
  // Ref to track previously answered question for history
  const previousAnsweredQuestionRef = useRef(null);

  // Add a ref to track the consecutive unanswered questions to avoid race conditions
  const consecutiveUnansweredRef = useRef(0);

  // Add a ref to track if the quiz has been stopped due to consecutive unanswered questions
  const quizStoppedRef = useRef(false);

  // Add a ref to track if a question has already timed out
  const questionTimedOutRef = useRef(false);

  // Simple string hash function to create reliable question IDs
  const hashString = (str) => {
    if (!str) return "0";
    
    // Convert to string if not already a string
    let text = String(str);
    
    // For very long strings, just use the first 100 chars for consistency
    if (text.length > 100) {
      text = text.substring(0, 100);
    }
    
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      const char = text.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16); // Convert to hex string
  };

  // Function to update and save user stats to localStorage
  const updateUserStats = (statUpdate) => {
    try {
      // Get the current stats or initialize with defaults
      const currentStats = JSON.parse(localStorage.getItem('userStats')) || {
        totalQuestions: 0,
        correctAnswers: 0,
        incorrectAnswers: 0,
        skippedQuestions: 0,
        currentStreak: 0,
        maxStreak: 0,
        topicsDistribution: {},
        dailyActivity: {},
        totalRizzPoints: 0,
        topicRizz: {},
        currentLevel: 0,
        difficultyStats: {
          easy: { count: 0, points: 0 },
          medium: { count: 0, points: 0 },
          hard: { count: 0, points: 0 }
        },
        averageResponseTime: 0,
        totalResponseTime: 0,
        // Add tracking of processed question IDs
        processedQuestionIds: {},
        // Add counter protection to detect rapid/excessive updates
        updateCounter: {
          lastReset: Date.now(),
          count: 0
        }
      };

      // Debug log
      console.log('Updating stats with:', statUpdate);

      // Get current streak data from both sources
      const streakData = JSON.parse(localStorage.getItem('techGuyGregStreak') || '{"current":0,"max":0}');
      const userStreakData = JSON.parse(localStorage.getItem('userStreak') || '{"current":0,"max":0}');
      
      // Create a deep copy of the current stats to avoid mutation issues
      const updatedStats = JSON.parse(JSON.stringify(currentStats));
      
      // Initialize or update tracking objects if they don't exist
      if (!updatedStats.processedQuestionIds) {
        updatedStats.processedQuestionIds = {};
      }
      
      if (!updatedStats.updateCounter) {
        updatedStats.updateCounter = {
          lastReset: Date.now(),
          count: 0
        };
      }
      
      // Counter protection - reset counter if it's been more than 10 minutes
      const now = Date.now();
      if (now - updatedStats.updateCounter.lastReset > 600000) {
        updatedStats.updateCounter = {
          lastReset: now,
          count: 0
        };
      }
      
      // Increment counter and handle excessive updates more gracefully
      updatedStats.updateCounter.count++;
      
      // If we're getting too many updates, instead of blocking completely:
      // 1. Still log the issue for debugging
      // 2. Allow important updates (correct/incorrect answers) to go through
      // 3. Skip auxiliary updates (topic distribution, etc.)
      if (updatedStats.updateCounter.count > 30) {
        console.warn("⚠️ High frequency of stats updates detected - throttling non-essential updates");
        
        // Only allow through important updates (correct/incorrect answers)
        const isEssentialUpdate = 
          statUpdate.correctAnswers !== undefined || 
          statUpdate.incorrectAnswers !== undefined ||
          statUpdate.isFirstCorrectAnswer || 
          statUpdate.isFirstIncorrectAnswer;
        
        // For non-essential updates, just save the counter and return
        if (!isEssentialUpdate) {
          // Still save the counter state to maintain throttling
          localStorage.setItem('userStats', JSON.stringify(updatedStats));
          return;
        }
      }
      
      // Skip questions explicitly marked as skipped - NEVER count them in totalQuestions
      if (statUpdate.skippedQuestions !== undefined && !statUpdate.isForceCount) {
        console.log("⏭️ Skipped question detected - only updating skippedQuestions counter");
        updatedStats.skippedQuestions += statUpdate.skippedQuestions;
        
        // Don't update any other counters for skipped questions
        // Just save and return
        localStorage.setItem('userStats', JSON.stringify(updatedStats));
        return;
      }
      
      // Check if this question has already been processed (if questionId is provided)
      if (statUpdate.questionId) {
        const questionHash = hashString(statUpdate.questionId);
        const currentTimestamp = Date.now();
        const lastProcessed = updatedStats.processedQuestionIds[questionHash];
        
        // Add debugging to track hash values
        console.log(`Question hash: ${questionHash} for question starting with: "${String(statUpdate.questionId).substring(0, 30)}..."`);
        
        // Special handling for first few questions - don't deduplicate them
        // Count how many questions we've processed total
        const totalProcessedQuestions = updatedStats.totalQuestions + updatedStats.skippedQuestions;
        const isFirstFewQuestions = totalProcessedQuestions < 3;
        
        // Skip deduplication checks completely if forceCount is set
        // This ensures correct/incorrect answers are always counted
        if (!statUpdate.forceCount) {
          // If we've already processed this question recently (within last hour), don't update stats again
          // UNLESS this is one of the first few questions (we're more lenient with those)
          if (lastProcessed && (currentTimestamp - lastProcessed < 3600000) && !isFirstFewQuestions) {
            console.log(`⚠️ Question ${String(statUpdate.questionId).substring(0, 20)}... (${questionHash}) already processed ${Math.round((currentTimestamp - lastProcessed)/1000)}s ago, skipping update`);
            
            // Special handling for topic-specific rizz points - update them even for duplicate questions
            if (statUpdate.topic && statUpdate.rizzPointsChange) {
              console.log(`🔄 Still updating topic-specific rizz for ${statUpdate.topic} despite duplicate question`);
              updatedStats.topicRizz = updatedStats.topicRizz || {};
              const previousTopicRizz = updatedStats.topicRizz[statUpdate.topic] || 0;
              updatedStats.topicRizz[statUpdate.topic] = previousTopicRizz + statUpdate.rizzPointsChange;
              console.log(`Topic rizz update for ${statUpdate.topic}: ${previousTopicRizz} -> ${updatedStats.topicRizz[statUpdate.topic]} (${statUpdate.rizzPointsChange})`);
              
              // Also update topic distribution if this is a topic distribution update
              if (statUpdate.topicsDistribution && statUpdate.topicsDistribution[statUpdate.topic]) {
                updatedStats.topicsDistribution = updatedStats.topicsDistribution || {};
                updatedStats.topicsDistribution[statUpdate.topic] = (updatedStats.topicsDistribution[statUpdate.topic] || 0) + statUpdate.topicsDistribution[statUpdate.topic];
                console.log(`Topic distribution update for ${statUpdate.topic}: ${updatedStats.topicsDistribution[statUpdate.topic]}`);
              }
              
              // Save the updated stats
              localStorage.setItem('userStats', JSON.stringify(updatedStats));
            }
            
            return;
          }
        } else {
          console.log(`✅ Force counting this question (hash: ${questionHash}), bypassing deduplication checks`);
        }
        
        // Add this question ID to the processed list with timestamp
        updatedStats.processedQuestionIds[questionHash] = currentTimestamp;
        
        // Clean up old entries (older than 24 hours)
        Object.keys(updatedStats.processedQuestionIds).forEach(key => {
          if (currentTimestamp - updatedStats.processedQuestionIds[key] > 86400000) {
            delete updatedStats.processedQuestionIds[key];
          }
        });
      }
      
      // Identify what type of update this is
      const isTopicDistributionOnly = statUpdate.topicsDistribution && 
        Object.keys(statUpdate).filter(key => key !== 'questionId' && key !== 'isAuxiliaryUpdate').length === 1;
      
      const isResponseTimeOnly = statUpdate.totalResponseTime && statUpdate.averageResponseTime &&
        Object.keys(statUpdate).filter(key => key !== 'questionId' && key !== 'isAuxiliaryUpdate').length === 2;
      
      const isAuxiliaryUpdate = statUpdate.isAuxiliaryUpdate || isTopicDistributionOnly || isResponseTimeOnly;
      
      // Log the type of update being processed
      if (statUpdate.isCorrectAnswer) {
        console.log("🎯 Processing a CORRECT answer stats update");
      } else if (statUpdate.incorrectAnswers !== undefined) {
        console.log("❌ Processing an INCORRECT answer stats update");
      } else if (statUpdate.skippedQuestions !== undefined) {
        console.log("⏭️ Processing a SKIPPED answer stats update");
      } else if (isTopicDistributionOnly) {
        console.log("📊 Processing a topic distribution update");
      } else if (isResponseTimeOnly) {
        console.log("⏱️ Processing a response time update");
      } else if (isAuxiliaryUpdate) {
        console.log("🔄 Processing an auxiliary update (no question counting)");
      }
      
      // Only update totalQuestions if this is not an auxiliary update
      if (statUpdate.totalQuestions !== undefined && !isAuxiliaryUpdate) {
        // Special handling for correct and incorrect answers: these are ALWAYS counted
        // even if they might be duplicates (this ensures first questions are counted)
        const isRealAnswer = statUpdate.isCorrectAnswer || statUpdate.incorrectAnswers !== undefined;
        
        console.log(`Incrementing totalQuestions from ${updatedStats.totalQuestions} to ${updatedStats.totalQuestions + statUpdate.totalQuestions} (isRealAnswer: ${isRealAnswer})`);
        updatedStats.totalQuestions += statUpdate.totalQuestions;
      }
      
      // Special handling for the very first correct answer - ALWAYS increment it
      const isFirstCorrectAnswer = updatedStats.correctAnswers === 0 && statUpdate.correctAnswers !== undefined;
      if (isFirstCorrectAnswer) {
        console.log("🎉 FIRST CORRECT ANSWER DETECTED! Ensuring it's counted.");
      }
      
      // Special handling for the very first incorrect answer - ALWAYS increment it
      const isFirstIncorrectAnswer = updatedStats.incorrectAnswers === 0 && statUpdate.incorrectAnswers !== undefined;
      if (isFirstIncorrectAnswer) {
        console.log("📌 FIRST INCORRECT ANSWER DETECTED! Ensuring it's counted.");
      }
      
      // Update other stats only if they are explicitly defined (not undefined)
      if (statUpdate.correctAnswers !== undefined) {
        console.log(`Incrementing correctAnswers from ${updatedStats.correctAnswers} to ${updatedStats.correctAnswers + statUpdate.correctAnswers}`);
        updatedStats.correctAnswers += statUpdate.correctAnswers;
        
        // If this was the first correct answer, save immediately to ensure it's recorded
        if (isFirstCorrectAnswer) {
          localStorage.setItem('userStats', JSON.stringify(updatedStats));
        }
      }
      
      if (statUpdate.incorrectAnswers !== undefined) {
        console.log(`Incrementing incorrectAnswers from ${updatedStats.incorrectAnswers} to ${updatedStats.incorrectAnswers + statUpdate.incorrectAnswers}`);
        updatedStats.incorrectAnswers += statUpdate.incorrectAnswers;
        
        // If this was the first incorrect answer, save immediately to ensure it's recorded
        if (isFirstIncorrectAnswer) {
          localStorage.setItem('userStats', JSON.stringify(updatedStats));
        }
      }
      
      if (statUpdate.skippedQuestions !== undefined) {
        console.log(`Incrementing skippedQuestions from ${updatedStats.skippedQuestions} to ${updatedStats.skippedQuestions + statUpdate.skippedQuestions}`);
        updatedStats.skippedQuestions += statUpdate.skippedQuestions;
      }
      
      // Handle rizz points update and level-up logic
      if (statUpdate.rizzPointsChange) {
        // Add rizz points to total
        updatedStats.totalRizzPoints += statUpdate.rizzPointsChange;
        
        // Update topic-specific rizz if a topic is provided
        if (statUpdate.topic) {
          updatedStats.topicRizz = updatedStats.topicRizz || {};
          const previousTopicRizz = updatedStats.topicRizz[statUpdate.topic] || 0;
          updatedStats.topicRizz[statUpdate.topic] = previousTopicRizz + statUpdate.rizzPointsChange;
          console.log(`Topic rizz update for ${statUpdate.topic}: ${previousTopicRizz} -> ${updatedStats.topicRizz[statUpdate.topic]} (${statUpdate.rizzPointsChange})`);
        }
        
        // Check for level-up
        const previousLevelData = calculateLevelFromRizz(updatedStats.totalRizzPoints - statUpdate.rizzPointsChange);
        const currentLevelData = calculateLevelFromRizz(updatedStats.totalRizzPoints);
        
        // If level increased, update the level and adjust rizz points
        if (currentLevelData.level > previousLevelData.level) {
          // Store the previous level for reference
          updatedStats.previousLevel = updatedStats.currentLevel;
          
          // Update to the new level
          updatedStats.currentLevel = currentLevelData.level;
          
          // Log the level-up event
          console.log(`Level up! From ${previousLevelData.level} to ${currentLevelData.level}`);
          console.log(`Title change: ${previousLevelData.title} to ${currentLevelData.title}`);
          
          // Store the level-up timestamp
          updatedStats.lastLevelUp = new Date().toISOString();
        }
      }
      
      // Special handling for first answers - ensure they are properly recorded
      if ((statUpdate.isFirstCorrectAnswer || statUpdate.isFirstIncorrectAnswer) && 
          (statUpdate.correctAnswers !== undefined || statUpdate.incorrectAnswers !== undefined)) {
        
        console.log("🚨 Using special handling for first answer - double-saving to localStorage");
        
        // Also save a backup copy with a different key
        localStorage.setItem('userStats_firstAnswer', JSON.stringify(updatedStats));
        
        // For first answers, also force an update of the in-memory score state
        setTimeout(() => {
          // This will run after the current call stack is complete
          try {
            const freshStats = JSON.parse(localStorage.getItem('userStats'));
            console.log("Verifying first answer was recorded:", freshStats);
            
            // If the stats don't reflect the first answer, force them again
            if ((statUpdate.correctAnswers && freshStats.correctAnswers < 1) ||
                (statUpdate.incorrectAnswers && freshStats.incorrectAnswers < 1)) {
              console.log("🔄 Re-applying first answer stats");
              localStorage.setItem('userStats', JSON.stringify(updatedStats));
            }
          } catch (e) {
            console.error("Error in first answer verification:", e);
          }
        }, 500);
      }
      
      // Sync streak data - use the highest values from all sources
      updatedStats.currentStreak = Math.max(
        updatedStats.currentStreak || 0,
        streakData.current || 0,
        userStreakData.current || 0,
        statUpdate.currentStreak || 0
      );
      
      updatedStats.maxStreak = Math.max(
        updatedStats.maxStreak || 0,
        streakData.max || 0,
        userStreakData.max || 0,
        statUpdate.maxStreak || 0
      );
      
      // Update topic distribution if provided
      if (statUpdate.topicsDistribution) {
        updatedStats.topicsDistribution = updatedStats.topicsDistribution || {};
        
        // Iterate through the topics in the update
        Object.entries(statUpdate.topicsDistribution).forEach(([topic, count]) => {
          // Add the count to the existing topic count (or initialize if it doesn't exist)
          updatedStats.topicsDistribution[topic] = (updatedStats.topicsDistribution[topic] || 0) + count;
          
          // If this is a topic distribution update with a topic field and rizzPointsChange,
          // ensure we update the topic-specific rizz points as well
          if (statUpdate.topic === topic && statUpdate.rizzPointsChange && statUpdate.isAuxiliaryUpdate) {
            console.log(`Updating topic-specific rizz for ${topic} in topic distribution update`);
            updatedStats.topicRizz = updatedStats.topicRizz || {};
            const previousTopicRizz = updatedStats.topicRizz[topic] || 0;
            updatedStats.topicRizz[topic] = previousTopicRizz + statUpdate.rizzPointsChange;
            console.log(`Topic distribution rizz update for ${topic}: ${previousTopicRizz} -> ${updatedStats.topicRizz[topic]} (${statUpdate.rizzPointsChange})`);
            
            // Force save to localStorage immediately to ensure it's persisted
            localStorage.setItem('userStats', JSON.stringify(updatedStats));
          }
        });
      }
      
      // Update response time statistics if provided
      if (statUpdate.totalResponseTime && statUpdate.averageResponseTime) {
        const prevAvg = updatedStats.averageResponseTime || 0;
        const prevCount = updatedStats.totalResponseTime || 0;
        
        // Calculate new average
        const newCount = prevCount + statUpdate.totalResponseTime;
        const newAvg = ((prevAvg * prevCount) + (statUpdate.averageResponseTime * statUpdate.totalResponseTime)) / newCount;
        
        updatedStats.totalResponseTime = newCount;
        updatedStats.averageResponseTime = newAvg;
      }
      
      // Update difficulty stats if provided
      if (statUpdate.difficulty && statUpdate.rizzPointsChange) {
        // Make sure difficultyStats exists
        updatedStats.difficultyStats = updatedStats.difficultyStats || {
          easy: { count: 0, points: 0 },
          medium: { count: 0, points: 0 },
          hard: { count: 0, points: 0 }
        };
        
        // Update the count and points for this difficulty
        const difficulty = statUpdate.difficulty.toLowerCase();
        if (updatedStats.difficultyStats[difficulty]) {
          updatedStats.difficultyStats[difficulty].count += 1;
          updatedStats.difficultyStats[difficulty].points += statUpdate.rizzPointsChange;
        }
      }
      
      // Save the updated stats to localStorage
      localStorage.setItem('userStats', JSON.stringify(updatedStats));
      
      // Also update the streak data in both localStorage items to keep them in sync
      const syncedStreakData = {
        current: updatedStats.currentStreak,
        max: updatedStats.maxStreak
      };
      
      localStorage.setItem('techGuyGregStreak', JSON.stringify(syncedStreakData));
      localStorage.setItem('userStreak', JSON.stringify(syncedStreakData));
      
      // Return the updated stats
      return updatedStats;
    } catch (error) {
      console.error("Error updating user stats:", error);
      return null;
    }
  };

  // Helper function to calculate level from rizz points
  const calculateLevelFromRizz = (rizzPoints) => {
    // Define title ranks with their level ranges - now with higher requirements
    const titleRanks = [
      { title: 'Novice', minLevel: 0, maxLevel: 0, rizzRequired: 0 },
      { title: 'Apprentice', minLevel: 1, maxLevel: 10, rizzRequired: 500 },  // Start at 500 (was 100)
      { title: 'Adept', minLevel: 11, maxLevel: 20, rizzRequired: 2000 },     // Was 1000
      { title: 'Specialist', minLevel: 21, maxLevel: 30, rizzRequired: 5000 }, // Was 2000
      { title: 'Expert', minLevel: 31, maxLevel: 40, rizzRequired: 10000 },    // Was 3500
      { title: 'Master', minLevel: 41, maxLevel: 50, rizzRequired: 20000 },    // Was 5500
      { title: 'Grandmaster', minLevel: 51, maxLevel: 60, rizzRequired: 35000 }, // Was 8000
      { title: 'Legendary', minLevel: 61, maxLevel: 70, rizzRequired: 55000 },   // Was 11000
      { title: 'Mythic', minLevel: 71, maxLevel: 80, rizzRequired: 80000 },      // Was 15000
      { title: 'Divine', minLevel: 81, maxLevel: 100, rizzRequired: 120000 }     // Was 20000
    ];
    
    // Calculate level requirements with steeper progression
    const levelRequirements = [];
    let previousLevelRizz = 0;
    
    titleRanks.forEach(rank => {
      if (rank.minLevel === 0) {
        levelRequirements.push({
          level: 0,
          title: rank.title,
          rizzRequired: 0,
          rizzForNextLevel: 500 // First level requires 500 points
        });
        previousLevelRizz = 0;
        return;
      }
      
      let levelRizz = rank.rizzRequired;
      const levelsInRank = rank.maxLevel - rank.minLevel + 1;
      
      for (let i = 0; i < levelsInRank; i++) {
        const level = rank.minLevel + i;
        // Steeper progression within ranks - each level gets 20% harder (was 15%)
        const levelMultiplier = 1 + (i * 0.2); 
        const rizzForThisLevel = Math.round(levelRizz * levelMultiplier / levelsInRank);
        
        previousLevelRizz += (i === 0 && level > 1) ? 0 : rizzForThisLevel;
        
        // Steeper progression between levels
        const nextLevelRizz = (i === levelsInRank - 1 && rank.title === 'Divine') 
          ? rizzForThisLevel * 2.0 // For the final level (100) - was 1.5
          : rizzForThisLevel * 1.25; // Normal progression - was 1.15
        
        levelRequirements.push({
          level,
          title: rank.title,
          rizzRequired: previousLevelRizz,
          rizzForNextLevel: Math.round(nextLevelRizz)
        });
      }
    });
    
    // Find current level based on total rizz points
    let currentLevel = 0;
    let currentTitle = 'Novice';
    let rizzToNextLevel = 500;
    let adjustedRizz = rizzPoints;
    
    for (let i = 1; i < levelRequirements.length; i++) {
      if (rizzPoints >= levelRequirements[i].rizzRequired) {
        currentLevel = levelRequirements[i].level;
        currentTitle = levelRequirements[i].title;
        adjustedRizz = rizzPoints - levelRequirements[i].rizzRequired;
        
        // Calculate rizz needed for next level
        if (i < levelRequirements.length - 1) {
          rizzToNextLevel = levelRequirements[i + 1].rizzRequired - levelRequirements[i].rizzRequired;
        } else {
          // For max level
          rizzToNextLevel = levelRequirements[i].rizzForNextLevel;
        }
      } else {
        // We've found the next level the user hasn't reached yet
        rizzToNextLevel = levelRequirements[i].rizzRequired - rizzPoints;
        break;
      }
    }
    
    return {
      level: currentLevel,
      title: currentTitle,
      adjustedRizz: adjustedRizz,
      rizzToNextLevel: rizzToNextLevel
    };
  };

  // Load initial score and streak from localStorage
  useEffect(() => {
    try {
      const existingStatsJson = localStorage.getItem('userStats');
      if (existingStatsJson) {
        const stats = JSON.parse(existingStatsJson);
        
        // Set the score from localStorage
        if (stats.correctAnswers !== undefined && stats.incorrectAnswers !== undefined) {
          setScore({
            correct: stats.correctAnswers || 0,
            wrong: stats.incorrectAnswers || 0
          });
        }
        
        // Set the streak from localStorage
        if (stats.maxStreak !== undefined) {
          setStreak(prev => ({
            ...prev,
            max: Math.max(prev.max, stats.maxStreak || 0)
          }));
        }
      }
    } catch (error) {
      console.error("Error loading user stats:", error);
    }
  }, []);

  // Enhanced duplicate detection function
  const isDuplicateQuizQuestion = (newQuestion) => {
    if (!newQuestion) return false;

    // Normalize the question for comparison (lowercase, remove punctuation, extra spaces)
    const normalizeText = (text) => {
      return text
        .toLowerCase()
        .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()?"']/g, "")  // Remove punctuation
        .replace(/\s+/g, " ")  // Replace multiple spaces with a single space
        .trim();
    };

    const normalizedNew = normalizeText(newQuestion);

    // Get "core" keywords by removing common filler words
    const getKeywords = (text) => {
      const fillerWords = [
        "the", "a", "an", "and", "or", "but", "in", "on", "at", "to", "for", "with",
        "about", "of", "by", "is", "are", "was", "were", "be", "been", "being",
        "what", "which", "who", "whom", "whose", "when", "where", "why", "how"
      ];

      return normalizeText(text)
        .split(" ")
        .filter(word => word.length > 2 && !fillerWords.includes(word))
        .sort();  // Sort to match regardless of word order
    };

    const newQuestionKeywords = getKeywords(newQuestion);

    // Calculate keyword overlap between two questions
    const calculateKeywordOverlap = (keywords1, keywords2) => {
      const overlap = keywords1.filter(word => keywords2.includes(word));
      const overlapScore = overlap.length / Math.max(1, Math.min(keywords1.length, keywords2.length));
      return overlapScore;
    };

    return askedQuestions.some(q => {
      // Skip invalid entries
      if (!q) return false;

      // Handle both string values and object values safely
      const questionText = typeof q === 'string' ? q : (q.text || q.question || "");

      // Make sure we have a string before processing
      if (!questionText || typeof questionText !== 'string') return false;

      // Check exact match or very close match first (fastest check)
      const normalizedQ = normalizeText(questionText);
      if (normalizedNew === normalizedQ) return true;

      // Use string similarity for close matches
      const similarity = stringSimilarity.compareTwoStrings(normalizedNew, normalizedQ);
      if (similarity >= 0.8) return true;  // Increase threshold for stricter matching

      // For questions with moderate similarity, check keyword overlap
      if (similarity >= 0.5) {
        const qKeywords = getKeywords(questionText);
        const keywordOverlap = calculateKeywordOverlap(newQuestionKeywords, qKeywords);

        // If there's significant keyword overlap, consider it a duplicate
        if (keywordOverlap >= 0.7) return true;
      }

      return false;
    });
  };

  // Better function to add questions to history with duplicate prevention
  const addQuestionToHistory = (question) => {
    if (!question) return;

    // Debug log the incoming question
    console.log("Adding to history:", {
      question: question.question ? question.question.substring(0, 30) + "..." : "No question text",
      isCorrect: question.isCorrect,
      isSkipped: question.isSkipped,
      userAnswer: question.userAnswer
    });

    setAskedQuestions(prev => {
      // Helper function to normalize question text for comparison
      const normalizeText = (text) => {
        if (!text || typeof text !== 'string') return '';
        return text
          .toLowerCase()
          .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()?"']/g, "")
          .replace(/\s+/g, " ")
          .trim();
      };

      // Extract the question text
      const newQuestionText = typeof question === 'string'
        ? question
        : (question.text || question.question || "");

      if (!newQuestionText) return prev;

      const normalizedNew = normalizeText(newQuestionText);

      // Check for existing duplicates
      const isDuplicate = prev.some(existing => {
        const existingText = typeof existing === 'string'
          ? existing
          : (existing.text || existing.question || "");

        return normalizeText(existingText) === normalizedNew;
      });

      if (isDuplicate) {
        console.log(`Question already exists in history: "${newQuestionText}"`);
        return prev;
      }

      // Ensure consistent object format and preserve all properties, especially isCorrect and isSkipped
      const processedQuestion = typeof question === 'string'
        ? { text: question, timestamp: Date.now() }
        : { 
            ...question, 
            timestamp: question.timestamp || Date.now(),
            // Explicitly ensure these properties are preserved with correct types
            isCorrect: question.isCorrect === true ? true : 
                      question.isCorrect === false ? false : null,
            isSkipped: question.isSkipped === true
          };

      // Log the processed question
      console.log("Processed question for history:", {
        isCorrect: processedQuestion.isCorrect,
        isSkipped: processedQuestion.isSkipped
      });

      // Force history component to update by toggling the flag
      setHistoryUpdateFlag(prev => prev + 1);
      
      // Add the new question and save to localStorage
      const newQuestions = [...prev, processedQuestion];
      
      try {
        // Convert to JSON and back to ensure all properties are serializable
        const jsonString = JSON.stringify(newQuestions.slice(-50));
        const parsed = JSON.parse(jsonString);
        
        // Verify the properties survived JSON serialization
        const lastQuestion = parsed[parsed.length - 1];
        console.log("Saved question properties:", {
          isCorrect: lastQuestion.isCorrect,
          isSkipped: lastQuestion.isSkipped
        });
        
        localStorage.setItem('techGuyGregAskedQuestions', jsonString);
      } catch (e) {
        console.error("Error saving questions to localStorage:", e);
      }
      
      return newQuestions;
    });
  };

  // Clean up any invalid items in localStorage
  useEffect(() => {
    try {
      const savedQuestions = localStorage.getItem('techGuyGregAskedQuestions');
      if (savedQuestions) {
        const parsed = JSON.parse(savedQuestions);
        if (Array.isArray(parsed)) {
          // Filter out any null or undefined values and ensure all items have valid text
          const cleanedQuestions = parsed.filter(q =>
            q && (typeof q === 'string' || (typeof q === 'object' && q.text))
          );
          localStorage.setItem('techGuyGregAskedQuestions', JSON.stringify(cleanedQuestions));
        }
      }
    } catch (error) {
      console.error("Error cleaning localStorage:", error);
      // Reset if there's an error
      localStorage.removeItem('techGuyGregAskedQuestions');
    }
  }, []);

  // Fix for mobile viewport height issues
  useEffect(() => {
    // Create references to store previous dimensions
    const previousWidthRef = { current: window.innerWidth };
    const previousHeightRef = { current: window.innerHeight };
    
    // Function to set the correct viewport height
    const setVH = () => {
      // First get the viewport height and multiply it by 1% to get a value for a vh unit
      let vh = window.innerHeight * 0.01;
      // Then set the value in the --vh custom property to the root of the document
      document.documentElement.style.setProperty('--vh', `${vh}px`);

      // Check if this is a keyboard appearance by comparing with previous height
      const isKeyboardChange = window.innerWidth === previousWidthRef.current && 
                              window.innerHeight !== previousHeightRef.current;
      
      // Only adjust the container height on orientation changes, not on keyboard appearance
      if (window.innerWidth <= 768 && !isKeyboardChange) {
        document.querySelector('.chat-container').style.height = `calc(var(--vh, 1vh) * 100)`;
      }
      
      // Store current dimensions for future comparison
      previousWidthRef.current = window.innerWidth;
      previousHeightRef.current = window.innerHeight;
    };

    // Set the height initially
    setVH();

    // Add event listener for orientation change only, not resize
    // This prevents the viewport from adjusting when the keyboard appears
    window.addEventListener('orientationchange', setVH);

    // Clean up the event listeners
    return () => {
      window.removeEventListener('orientationchange', setVH);
    };
  }, []);

  // Save questions to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('techGuyGregAskedQuestions', JSON.stringify(askedQuestions));
  }, [askedQuestions]);

  // Auto-scroll to bottom when messages update
  useEffect(() => {
    chatRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, currentQuiz]);

  // Effect to add previous answered question to history when a new quiz starts
  useEffect(() => {
    // When a new quiz is loaded and we have a previous answered question
    if (currentQuiz && previousAnsweredQuestionRef.current) {
      // Check if this question is already in the history before adding it
      const questionToAdd = previousAnsweredQuestionRef.current;
      const questionText = questionToAdd.question || "";
      
      // Only add if it's not already in the history
      const isAlreadyInHistory = askedQuestions.some(existing => {
        const existingText = existing.question || existing.text || "";
        return existingText === questionText;
      });
      
      if (!isAlreadyInHistory) {
        // Now we add the previous question to history
        addQuestionToHistory(questionToAdd);
      }
      
      // Clear the ref for next time
      previousAnsweredQuestionRef.current = null;
    }
  }, [currentQuiz, askedQuestions]);

  // Enhanced quiz question fetching with improved retry logic
  const fetchNewQuizQuestion = async (retryCount = 0, useStrictFormat = false, prevAttempts = []) => {
    // Add a request-in-progress flag to prevent duplicate calls
    if (fetchInProgressRef.current && retryCount === 0) {
      console.log("⚠️ Quiz fetch already in progress, ignoring duplicate request");
      return null;
    }
    
    // Set the in-progress flag for the initial request (not for retries)
    if (retryCount === 0) {
      fetchInProgressRef.current = true;
    }
    
    const maxRetries = 3;
    // Remove excessive logging
    if (retryCount > 0) {
      console.log(`Retry attempt ${retryCount + 1} of ${maxRetries + 1}`);
    }

    try {
      // Set loading state at the beginning of the request
      if (retryCount === 0) {
        setIsLoading(true);
      }
      
      // Set quiz start time when a new question is fetched
      quizStartTimeRef.current = Date.now();

      // Track all attempted questions to avoid repeating on retries
      const currentAttempts = [...prevAttempts];

      // Get all covered subtopics for the current topic to request new ones
      const coveredSubtopics = [];
      if (topicCoverage && topicCoverage[topic] && topicCoverage[topic].subtopics) {
        Object.keys(topicCoverage[topic].subtopics).forEach(subtopic => {
          coveredSubtopics.push(subtopic);
        });
      }

      // Generate a list of subtopics we want to focus on (uncovered or less covered)
      let subtopicPrompt = "";
      
      // Basic subtopic keywords for each topic area
      const subtopicKeywords = {
        "leetcode": ["arrays", "strings", "linked lists", "trees", "graphs", "dynamic programming", "sorting", "searching", "backtracking", "greedy algorithms", "binary search", "hash tables", "two pointers", "sliding window", "recursion"],
        "ds&a": ["arrays", "linked lists", "stacks", "queues", "trees", "graphs", "hash tables", "heaps", "sorting algorithms", "searching algorithms", "time complexity", "space complexity", "recursion", "dynamic programming", "greedy algorithms"],
        "oop": ["classes", "objects", "inheritance", "polymorphism", "encapsulation", "abstraction", "interfaces", "composition", "design patterns", "SOLID principles", "method overloading", "method overriding", "constructors", "destructors", "access modifiers"],
        "operating systems": ["processes", "threads", "scheduling", "memory management", "file systems", "virtualization", "concurrency", "deadlocks", "synchronization", "I/O systems", "protection", "security", "distributed systems", "virtual memory", "kernel"],
        "databases": ["sql", "nosql", "relational databases", "joins", "indexes", "transactions", "normalization", "acid properties", "query optimization", "database design", "ER diagrams", "stored procedures", "triggers", "views", "data warehousing"],
        "cs fundamentals": ["data types", "variables", "control structures", "functions", "algorithms", "data structures", "complexity analysis", "binary representation", "boolean logic", "computer architecture", "programming paradigms", "compilation", "interpretation", "memory allocation", "garbage collection"],
        "system design": ["scalability", "reliability", "availability", "maintainability", "microservices", "distributed systems", "load balancing", "caching", "database sharding", "api design", "message queues", "rate limiting", "data partitioning", "consistency patterns", "cap theorem"],
        "networking": ["tcp/ip", "http", "dns", "routing", "protocols", "sockets", "osi model", "ip addressing", "subnetting", "network security", "firewalls", "load balancing", "cdn", "rest api", "websockets"],
        "finance resource management": ["healthcare budgeting", "resource allocation", "cost-benefit analysis", "financial planning", "healthcare economics", "reimbursement models", "staffing optimization", "financial reporting", "healthcare policy", "strategic financial management"],
        "pediatric nursing": ["growth and development", "pediatric assessment", "childhood illnesses", "pediatric medications", "family-centered care", "pediatric emergency", "congenital disorders", "immunizations", "pediatric nutrition", "pediatric pain management"],
        "maternity and newborn": ["prenatal care", "labor and delivery", "postpartum care", "newborn assessment", "high-risk pregnancy", "breastfeeding", "maternal complications", "neonatal complications", "family planning", "maternal-infant bonding"]
      };
      
      // Find the best matching general topic
      const closestGeneralTopic = Object.keys(subtopicKeywords).find(t => 
        topic.toLowerCase().includes(t.toLowerCase())
      );
      
      if (closestGeneralTopic) {
        const possibleSubtopics = subtopicKeywords[closestGeneralTopic];
        
        // Find uncovered or less covered subtopics
        const uncoveredSubtopics = possibleSubtopics.filter(s => !coveredSubtopics.includes(s));
        
        if (uncoveredSubtopics.length > 0) {
          // Randomly select 1-3 uncovered subtopics to focus on
          const selectedSubtopics = uncoveredSubtopics
            .sort(() => 0.5 - Math.random())
            .slice(0, Math.min(3, uncoveredSubtopics.length));
          
          subtopicPrompt = ` focusing on ${selectedSubtopics.join(', ')}`;
        } else {
          // If all subtopics are covered, select the least covered ones based on count
          if (topicCoverage && topicCoverage[topic] && topicCoverage[topic].subtopics) {
            const subtopicCounts = Object.entries(topicCoverage[topic].subtopics)
              .map(([name, count]) => ({ name, count }))
              .sort((a, b) => a.count - b.count)
              .slice(0, 3)
              .map(item => item.name);
            
            if (subtopicCounts.length > 0) {
              subtopicPrompt = ` focusing on ${subtopicCounts.join(', ')}`;
            }
          }
        }
      }

      // Adjust the prompt to encourage greater variety based on retry count
      const isNursingTopic = topic.toLowerCase().includes("nursing") || 
                            topic.toLowerCase().includes("maternity") || 
                            topic.toLowerCase().includes("finance resource management");
      
      const isComputerScienceTopic = !isNursingTopic;
      
      const topicVariations = isNursingTopic ? 
        [
          `${topic}${subtopicPrompt} for MSN (Master of Science in Nursing) exams`, 
          `a specific aspect of ${topic}${subtopicPrompt} commonly tested in MSN certification exams`,
          `an advanced concept in ${topic}${subtopicPrompt} relevant to MSN clinical practice`,
          `a fundamental principle of ${topic}${subtopicPrompt} essential for MSN students`
        ] : 
        [
          `${topic}${subtopicPrompt}`, // First try: use topic with subtopic guidance
          `a specific aspect of ${topic}${subtopicPrompt}`, // Second try: more specific
          `an advanced concept in ${topic}${subtopicPrompt}`, // Third try: more advanced
          `a fundamental principle of ${topic}${subtopicPrompt}` // Last try: more fundamental
        ];

      // Build a list of previously asked questions to avoid repetition
      const previousQuestionsPrompt = askedQuestions.length > 0 ? 
        `\n\nDo NOT ask any of these previous questions: ${
          askedQuestions
            .slice(-10) // Use most recent 10 questions
            .map(q => typeof q === 'string' ? q : (q.question || q.text || ""))
            .filter(Boolean)
            .map(q => `"${q.substring(0, 50)}${q.length > 50 ? '...' : ''}"`)
            .join(', ')
        }` :
        '';

      // Add historical coverage to avoid repeating similar topics
      const sessionId = localStorage.getItem('quizSessionId') || Date.now().toString();
      if (!localStorage.getItem('quizSessionId')) {
        localStorage.setItem('quizSessionId', sessionId);
      }

      // More explicit instructions for proper quiz format with increasing specificity
      const quizPrompt = useStrictFormat ?
        `Create a ${isNursingTopic ? 'nursing' : 'computer science'} quiz question on ${topicVariations[Math.min(retryCount, topicVariations.length - 1)]} with EXACTLY this format:

Question: [Your question here]
A) [First option]
B) [Second option]
C) [Third option]
D) [Fourth option]
Correct Answer: [ONE letter: A, B, C, or D]

${isNursingTopic ? 'IMPORTANT: This question should be at the MSN (Master of Science in Nursing) level and similar to questions found on nursing certification exams. Make it clinically relevant and scenario-based when appropriate.' : 'IMPORTANT: This question should be at the level of a computer science undergraduate or graduate student preparing for technical interviews or exams.'}
CRITICAL: Include all 4 options A through D, and ensure the correct answer is one of those letters.
IMPORTANT: The question must be UNIQUE and different from any previously asked questions.${previousQuestionsPrompt}${
        retryCount > 0 ? '\nPlease make this question substantially different from your previous responses.' : ''
      }` :
        `Generate a new unique ${isNursingTopic ? 'nursing' : 'computer science'} quiz question on ${topicVariations[Math.min(retryCount, topicVariations.length - 1)]} with four answer choices. ${isNursingTopic ? 'Make it appropriate for MSN-level nursing students and similar to questions on nursing certification exams.' : 'Make it appropriate for computer science students preparing for technical interviews or exams.'} Make sure it's different from previous questions.${previousQuestionsPrompt}`;

      // Add a requestId to prevent race conditions with multiple API calls
      const requestId = Date.now().toString();
      
      // Reduce logging - just keep this essential log
      console.log(`Fetching new question on ${topic}...`);

      // Call the API to get a quiz question
      const response = await fetch("/api/openai", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: quizPrompt,
          topic,
          isQuiz: true,
          requestUniqueQuestion: true, // Always request unique questions
          requestId,
          isLeetCodeTopic: topic.toLowerCase().includes("leetcode"),
          sessionId // Pass session ID for tracking
        }),
      });

      const data = await response.json();

      // Check if the response is empty or has an error
      if (!data || !data.text) {
        console.error("Empty response from API");
        if (retryCount < maxRetries) {
          return fetchNewQuizQuestion(retryCount + 1, true, currentAttempts);
        }
        return null;
      }

      // Parse the quiz data
      const quizData = parseQuiz(data.text);

      // If parsing failed, retry with stricter formatting
      if (!quizData) {
        console.error("Failed to parse quiz data, retrying with stricter format");
        if (retryCount < maxRetries) {
          return fetchNewQuizQuestion(retryCount + 1, true, currentAttempts);
        }
        return null;
      }

      // Check if this question is too similar to previously asked ones
      if (isDuplicateQuizQuestion(quizData.question)) {
        console.log("Duplicate question detected, retrying...");
        if (retryCount < maxRetries) {
          // Add this attempt to the list to avoid similar questions
          currentAttempts.push(quizData.question);
          return fetchNewQuizQuestion(retryCount + 1, useStrictFormat, currentAttempts);
        }
      }

      // Add to asked questions list and store in localStorage
      setAskedQuestions(prev => {
        // Don't add the new quiz question to history yet - we'll add it after it's answered
        // This prevents unanswered questions from appearing in history
        return prev;
      });

      // Add previous answered question to history if there is one
      if (previousAnsweredQuestionRef.current) {
        addQuestionToHistory(previousAnsweredQuestionRef.current);
        previousAnsweredQuestionRef.current = null;
      }

      // Successfully got a new quiz
      return quizData;

    } catch (error) {
      console.error("Error fetching quiz question:", error);
      if (retryCount < maxRetries) {
        return fetchNewQuizQuestion(retryCount + 1, true, prevAttempts);
      }
      return null;
    } finally {
      // Only reset flags if this is the original call or the final retry
      if (retryCount === 0 || retryCount === maxRetries) {
        setIsLoading(false);
        fetchInProgressRef.current = false;
      }
    }
  };

// Add debouncing mechanism to prevent multiple rapid clicks
let isQuizButtonLocked = false;
const DEBOUNCE_TIMEOUT = 1000; // 1 second lockout

  // Timer for quiz questions - using timerDuration
  useEffect(() => {
    // Don't run the timer if the quiz has been stopped
    if (quizStoppedRef.current) {
      console.log("Quiz has been stopped. Not running timer.");
      return;
    }
    
    if (currentQuiz) {
      setTimeLeft(timerDuration);
      
      // Reset the question timed out flag when a new quiz starts
      questionTimedOutRef.current = false;
      
      // Set up visibility detection
      const { isVisible, cleanup } = setupVisibilityDetection((newVisibility) => {
        console.log(`Quiz timer visibility changed: ${newVisibility ? 'visible' : 'hidden'}`);
      });
      
      // Variable to track if the page is visible
      let pageIsVisible = isVisible;
      
      const timerInterval = setInterval(() => {
        // Only decrement timer if page is visible
        if (pageIsVisible) {
          setTimeLeft(prev => {
            if (prev <= 1) {
              clearInterval(timerInterval);
              console.log("Time's up for quiz:", currentQuiz.question);

              // Only increment the counter if this question hasn't already timed out
              if (!questionTimedOutRef.current) {
                // Set the flag to indicate this question has timed out
                questionTimedOutRef.current = true;
                
                // Increment consecutive unanswered questions counter
                const newCount = consecutiveUnansweredRef.current + 1;
                consecutiveUnansweredRef.current = newCount;
                setConsecutiveUnanswered(newCount);
                console.log(`Consecutive unanswered questions: ${newCount}`);

                // Check if we've reached the limit for consecutive questions
                if (newCount > 3) {
                  console.log(`Consecutive questions limit reached (${newCount}). Stopping quiz.`);
                  
                  // Add messages to indicate the quiz has been stopped
                  setMessages(prev => {
                    const lastMessage = prev[prev.length - 1];
                    
                    // If the last message is already a "Time's up!" message, update it
                    if (lastMessage && lastMessage.sender === "ai" && 
                        lastMessage.text.startsWith("Time's up! You didn't answer in time.")) {
                      // Update the existing "Time's up!" message with the new count
                      const updatedMessages = [...prev];
                      updatedMessages[updatedMessages.length - 1] = {
                        ...lastMessage,
                        text: `Time's up! You didn't answer in time. Consecutive unanswered: ${newCount}/4`
                      };
                      
                      // Add the "inactive" message
                      return [
                        ...updatedMessages,
                        {
                          text: "You've been inactive for too long. Please press 'Start Quiz' to begin a new quiz.",
                          sender: "ai"
                        }
                      ];
                    }
                    
                    // If there's no "Time's up!" message, add both messages
                    return [
                      ...prev,
                      { text: `Time's up! You didn't answer in time. Consecutive unanswered: ${newCount}/4`, sender: "ai" },
                      {
                        text: "You've been inactive for too long. Please press 'Start Quiz' to begin a new quiz.",
                        sender: "ai"
                      }
                    ];
                  });

                  // Set the quiz stopped flag
                  quizStoppedRef.current = true;
                  
                  setCurrentQuiz(null);
                  setConsecutiveUnanswered(0);
                  consecutiveUnansweredRef.current = 0;
                  console.log("Quiz stopped and consecutive questions counter reset to 0");
                  setTimeLeft(0);
                  
                  // Skip fetching a new question
                  return 0;
                } else {
                  console.log(`Consecutive questions limit not reached yet (${newCount}/4). Continuing quiz.`);
                  
                  // Update score and streak
                  setScore(prevScore => {
                    const newScore = { ...prevScore, wrong: prevScore.wrong + 1 };
                    
                    // Update user stats in localStorage for timed out questions
                    updateUserStats({
                      // Don't count skipped questions in the total count
                      skippedQuestions: 1,
                      currentStreak: 0,
                      questionId: currentQuiz?.question // Use question ID for deduplication
                    });
                    
                    return newScore;
                  });
                  
                  setStreak(prev => ({ ...prev, current: 0 }));
                  
                  // Add or update the "Time's up!" message
                  setMessages(prev => {
                    const lastMessage = prev[prev.length - 1];
                    if (lastMessage && lastMessage.sender === "ai" && 
                        lastMessage.text.startsWith("Time's up! You didn't answer in time.")) {
                      // Update the existing "Time's up!" message with the new count
                      const updatedMessages = [...prev];
                      updatedMessages[updatedMessages.length - 1] = {
                        ...lastMessage,
                        text: `Time's up! You didn't answer in time. Consecutive unanswered: ${newCount}/4`
                      };
                      return updatedMessages;
                    }
                    // Add a new "Time's up!" message
                    return [
                      ...prev,
                      { text: `Time's up! You didn't answer in time. Consecutive unanswered: ${newCount}/4`, sender: "ai" }
                    ];
                  });
                  
                  // Update daily activity for timed out questions
                  updateDailyActivity("skipped");
                  
                  // Save the timed-out question to history with isSkipped=true
                  if (currentQuiz) {
                    const timedOutQuestion = {
                      ...currentQuiz,
                      userAnswer: "SKIPPED",
                      isCorrect: null, // Not correct or incorrect
                      isSkipped: true, // Explicitly mark as skipped
                      timestamp: Date.now(),
                      responseTime: timerDuration, // They used the full time
                      topic: topic,
                      difficulty: "medium" // Default difficulty for timed out
                    };
                    
                    // Log the timed-out question
                    console.log("Timed out question:", {
                      question: timedOutQuestion.question.substring(0, 30) + "...",
                      isSkipped: timedOutQuestion.isSkipped,
                      isCorrect: timedOutQuestion.isCorrect
                    });
                    
                    // Add directly to history instead of using the ref
                    // This ensures it's added immediately with the correct properties
                    addQuestionToHistory(timedOutQuestion);
                  }
                  
                  // Fetch a new question
                  fetchNewQuizQuestion().then(newQuizData => {
                    // Double-check that we haven't stopped the quiz due to consecutive unanswered questions
                    if (quizStoppedRef.current) {
                      console.log("Quiz was stopped due to consecutive unanswered questions. Not setting a new quiz.");
                      return;
                    }
                    
                    if (newQuizData) {
                      console.log("Setting new quiz from separate fetch:", newQuizData.question);
                      setCurrentQuiz(newQuizData);
                      // Reset the question timed out flag for the new question
                      questionTimedOutRef.current = false;
                    } else {
                      // If we couldn't get a new quiz, make sure we reset the loading state
                      console.warn("Failed to fetch new quiz question");
                    }
                  });
                }
              } else {
                console.log("Question has already timed out. Not incrementing counter again.");
              }
              return 0;
            }
            return prev - 1;
          });
        }
      }, 1000);
      
      // Cleanup function
      return () => {
        clearInterval(timerInterval);
        cleanup(); // Clean up visibility listener
      };
    }
  }, [currentQuiz, timerDuration, consecutiveUnanswered]);

  // Update daily activity tracking
  const updateDailyActivity = (activityType) => {
    try {
      const today = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
      console.log(`Updating daily activity for date: ${today}, type: ${activityType}`);
      
      // Get existing stats
      const existingStatsJson = localStorage.getItem('userStats');
      let stats = existingStatsJson ? JSON.parse(existingStatsJson) : { dailyActivity: {} };
      
      // Ensure dailyActivity exists
      if (!stats.dailyActivity) {
        stats.dailyActivity = {};
      }
      
      // Initialize today's activity if not present
      if (!stats.dailyActivity[today]) {
        stats.dailyActivity[today] = {
          correct: 0,
          incorrect: 0,
          skipped: 0,
          total: 0,
          topicDistribution: {}
        };
      }
      
      // Update activity count
      stats.dailyActivity[today][activityType] = (stats.dailyActivity[today][activityType] || 0) + 1;
      stats.dailyActivity[today].total += 1;
      
      // Update topic distribution if we have a current quiz
      if (currentQuiz && topic) {
        stats.dailyActivity[today].topicDistribution[topic] = 
          (stats.dailyActivity[today].topicDistribution[topic] || 0) + 1;
      }
      
      // Keep only the last 90 days of activity
      const keepDays = 90;
      const dates = Object.keys(stats.dailyActivity).sort();
      if (dates.length > keepDays) {
        const datesToRemove = dates.slice(0, dates.length - keepDays);
        datesToRemove.forEach(date => {
          delete stats.dailyActivity[date];
        });
      }
      
      // Save the updated stats
      localStorage.setItem('userStats', JSON.stringify(stats));
    } catch (error) {
      console.error("Error updating daily activity:", error);
    }
  };

  // Clean up old questions
  const cleanupOldQuestions = () => {
    const ONE_WEEK = 7 * 24 * 60 * 60 * 1000; // one week in milliseconds
    const now = Date.now();

    setAskedQuestions(prev =>
      prev.filter(q => !q.timestamp || now - q.timestamp < ONE_WEEK)
    );
  };

  // Run cleanup on mount
  useEffect(() => {
    cleanupOldQuestions();
    // Set up periodic cleanup
    const cleanupInterval = setInterval(cleanupOldQuestions, 24 * 60 * 60 * 1000); // daily

    return () => clearInterval(cleanupInterval);
  }, []);

  // Handler for timer selection
  const handleTimerSelect = (duration) => {
    setTimerDuration(duration);
  };

  // Handle quiz-related questions
  const handleQuizHelp = async (question) => {
    const trimmedInput = question.trim();
    if (!trimmedInput) return;

    if (!currentQuiz) return;

    setMessages(prev => [...prev, { text: trimmedInput, sender: "user" }]);

    // Store the current input value before clearing it
    const currentInputValue = trimmedInput;

    // Clear the input field BEFORE setting isLoading
    setInput("");

    // Now set loading state after clearing input
    setIsLoading(true);

    try {
      // Prepare a message that includes context about the current quiz
      const helpMessage = {
        message: `${currentInputValue}\n\nContext: I'm currently answering this question: "${currentQuiz.question}"`,
        topic,
        isChat: true,
        isQuizHelp: true  // Flag to indicate this is help for a quiz
      };

      const response = await fetch("/api/openai", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(helpMessage),
      });

      const data = await response.json();
      if (data.text) {
        // Add a small delay to simulate thinking about the hint
        setTimeout(() => {
          // Display the AI help as a special message type
          setMessages(prev => [...prev, {
            text: data.text,
            sender: "ai",
            type: "quiz-help"  // Special type for quiz help messages
          }]);
        }, 1000); // 1 second delay
      }
    } catch (error) {
      console.error("Error getting quiz help:", error);
      setMessages(prev => [
        ...prev,
        { text: "Sorry, I couldn't provide help for this question right now.", sender: "ai" },
      ]);
    } finally {
      // Set isLoading to false after a short delay to match the response timing
      setTimeout(() => {
        setIsLoading(false);
      }, 800); // Slightly shorter than the response delay
    }
  };

  // Handle quiz button click
  const handleStartQuiz = async () => {
    // Reset consecutive unanswered counter when starting a new quiz
    setConsecutiveUnanswered(0);
    consecutiveUnansweredRef.current = 0;
    console.log("Consecutive unanswered counter reset to 0 (start quiz)");
    
    // Reset the quiz stopped flag
    quizStoppedRef.current = false;
    console.log("Quiz stopped flag reset to false (start quiz)");

    // Reset the question timed out flag
    questionTimedOutRef.current = false;
    console.log("Question timed out flag reset to false (start quiz)");

    if (currentQuiz) {
      alert("Please answer the current question before starting a new quiz.");
      return;
    }

    setMessages(prev => [...prev, {
      text: `Starting a new quiz on ${topic}! Get ready...`,
      sender: "ai"
    }]);

    // Fetch and display a new quiz question
    const quizData = await fetchNewQuizQuestion();
    if (quizData) {
      setCurrentQuiz(quizData);
    }
  };

  // Handle sending a chat message (for help, not for quiz)
  const handleSend = async () => {
    const trimmedInput = input.trim();
    if (!trimmedInput) return;

    // If there's an active quiz, treat this as a help request
    if (currentQuiz) {
      handleQuizHelp(trimmedInput);
      return;
    }

    // Normal chat flow continues below
    setMessages(prev => [...prev, { text: trimmedInput, sender: "user" }]);

    // Store the current input value before clearing it
    const currentInputValue = trimmedInput;

    // Clear the input field BEFORE setting isLoading - FIX
    setInput("");

    // Now set loading state after clearing input - FIX
    setIsLoading(true);

    try {
      const response = await fetch("/api/openai", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: currentInputValue,
          topic,
          isChat: true
        }),
      });

      const data = await response.json();
      if (data.text) {
        // Just display the response as a chat message
        setMessages(prev => [...prev, { text: data.text, sender: "ai" }]);
      }
    } catch (error) {
      console.error("Error:", error);
      setMessages(prev => [
        ...prev,
        { text: "Sorry, there was an error getting a response.", sender: "ai" },
      ]);
    } finally {
      // Ensure isLoading is set to false in all cases - FIX
      setIsLoading(false);
    }
  };

 // Handle answering a quiz question with improved synchronization
 const handleQuizAnswer = async (userAnswer) => {
   // Prevent duplicate processing - add a protection
   if (isAnsweringRef.current) {
     console.log("⚠️ Already processing an answer, ignoring duplicate submission");
     return;
   }
   
   // Reset consecutive unanswered counter when user answers
   setConsecutiveUnanswered(0);
   consecutiveUnansweredRef.current = 0;
   console.log("Consecutive unanswered counter reset to 0 (user answered)");
   
   // Reset the quiz stopped flag
   quizStoppedRef.current = false;
   console.log("Quiz stopped flag reset to false (user answered)");

   // Reset the question timed out flag
   questionTimedOutRef.current = false;
   console.log("Question timed out flag reset to false (user answered)");

   if (!currentQuiz) return;
   const pendingQuiz = {...currentQuiz}; // Create a copy to avoid race conditions
   
   // Check if this is a skipped question (empty answer)
   const isSkipped = !userAnswer || userAnswer.trim() === '';
   
   try {
     // Make sure we're not already processing an answer
     if (!isAnswering) {
       // Set both state and ref to prevent race conditions
       setIsAnswering(true);
       isAnsweringRef.current = true;
       
       // Calculate response time in seconds
       const responseTime = quizStartTimeRef.current ? (Date.now() - quizStartTimeRef.current) / 1000 : null;
       quizStartTimeRef.current = null; // Reset for next question

       // Determine question difficulty - use try/catch to prevent errors
       let questionDifficulty = 'medium'; // Default if function fails
       try {
         questionDifficulty = determineQuestionDifficulty(pendingQuiz);
       } catch (e) {
         console.error("Error determining difficulty:", e);
       }
       
       // Extract subtopics from the question (if available)
       let subtopics = [];
       if (pendingQuiz.subtopics && Array.isArray(pendingQuiz.subtopics)) {
         subtopics = pendingQuiz.subtopics;
       } else if (pendingQuiz.question) {
         // Try to extract subtopic keywords from the question if not explicitly provided
         const questionText = pendingQuiz.question.toLowerCase();
         
         // Common subtopics based on the main topic
         const subtopicKeywords = {
           "LeetCode": ["arrays", "strings", "linked lists", "trees", "graphs", "dynamic programming", "recursion", "sorting", "searching"],
           "DS&A": ["arrays", "linked lists", "trees", "graphs", "stacks", "queues", "hash tables", "heaps", "sorting", "searching", "recursion", "dynamic programming"],
           "OOP": ["classes", "objects", "inheritance", "polymorphism", "encapsulation", "abstraction", "interfaces", "design patterns"],
           "Operating Systems": ["processes", "threads", "scheduling", "memory management", "file systems", "virtualization", "concurrency"],
           "Databases": ["sql", "nosql", "indexing", "transactions", "normalization", "query optimization", "acid", "sharding"],
           "CS Fundamentals": ["algorithms", "data structures", "complexity", "binary", "logic", "architecture", "compilers"],
           "System Design": ["scalability", "reliability", "availability", "caching", "load balancing", "microservices", "api design"],
           "Networking": ["tcp/ip", "http", "dns", "routing", "protocols", "sockets", "security"],
           "Finance Resource Management": ["budgeting", "forecasting", "investment", "risk management", "financial analysis", "accounting"],
           "Pediatric Nursing": ["fluid medication calculation", "health promotion", "communicable diseases", "immunization", "growth and development", "genetics", "prematurity", "neurofocus", "common illnesses", "cardiac", "respiratory", "gi/gu", "orthopedic", "hematology", "endocrine", "mental health", "child development", "pediatric assessment", "medications", "family-centered care", "nutrition"],
           "Maternity & Newborn Nursing": ["postpartum", "newborn", "female reproductive system", "conception", "efm", "electronic fetal monitoring", "embryonic development", "fetal development", "prenatal care", "healthy pregnancy", "antepartum", "genetic testing", "labor and birth", "intrapartum", "labor augmentation", "pain management", "common complications", "intrapartum complications", "diabetes in pregnancy", "antepartum complications", "postpartum complications", "newborn complications", "women's health", "menopause", "andropause", "osteoporosis", "infertility", "pregnancy", "labor", "delivery", "breastfeeding"]
         };
         
         // Get keywords for the current topic, or use an empty array if not found
         const currentTopicKeywords = subtopicKeywords[topic] || [];
         
         // Filter keywords that appear in the question text
         subtopics = currentTopicKeywords.filter(keyword => questionText.includes(keyword.toLowerCase()));
         
         // If no subtopics found, use a generic one based on difficulty
         if (subtopics.length === 0) {
           subtopics = [`${questionDifficulty} question`];
         }
       }
       
       // Update topic coverage for each subtopic
       subtopics.forEach(subtopic => {
         updateTopicCoverage(topic, subtopic);
       });
       
       // Determine if the answer is correct (skipped answers are neither correct nor incorrect)
       const isCorrect = !isSkipped && userAnswer.trim().toUpperCase() === pendingQuiz.correctAnswer.trim().toUpperCase();
       
       // Get rizz points based on difficulty and correctness (no rizz points for skipped questions)
       const rizzPointsChange = isSkipped ? 0 : getRizzPointsForDifficulty(questionDifficulty, isCorrect);
       
       // Directly update rizz points (immediately, not in a callback)
       // Important: Get current rizz points from state rather than using the variable 
       // which might be stale due to React's state update batching
       setRizzPoints(currentRizzPoints => {
         const newRizzPoints = currentRizzPoints + rizzPointsChange;
         console.log(`Rizz points update: ${currentRizzPoints} -> ${newRizzPoints} (${rizzPointsChange})`);
         // Store in localStorage immediately after updating
         localStorage.setItem('userRizzPoints', newRizzPoints.toString());
         return newRizzPoints;
       });
       
       // Show animation for rizz points change
       setRizzPointsChange(rizzPointsChange);
       setRizzAnimating(true);
       
       // Show particles effect based on whether points were gained or lost
       if (rizzPointsChange !== 0) {
         showParticles(rizzPointsChange > 0 ? 'positive' : 'negative');
       }
       
       // Reset animation after a longer delay to ensure it's visible
       setTimeout(() => {
         setRizzAnimating(false);
       }, 2200);
       
       // Keep the points change visible for longer, then fade it out
       setTimeout(() => {
         setRizzPointsChange(0);
       }, 2500);

       // Update scores
       if (isCorrect) {
         console.log("✅ CORRECT ANSWER DETECTED!");
         
         setScore(prev => {
           const newCorrect = prev.correct + 1;
           console.log(`Score updated: correct answers: ${prev.correct} -> ${newCorrect}`);
           return { ...prev, correct: newCorrect };
         });
         
         // Update streak state
         setStreak(prev => {
           const newCurrent = prev.current + 1;
           const newMax = newCurrent > prev.max ? newCurrent : prev.max;
           const newStreak = { current: newCurrent, max: newMax };
           
           // Save to localStorage
           localStorage.setItem('userStreak', JSON.stringify(newStreak));
           
           return newStreak;
         });
         
         // Update daily activity for correct answers
         updateDailyActivity("correct");
         
         // Update user stats with correct answer, rizz points, and current streak
         console.log("Updating stats for CORRECT answer");
         
         // Get the current stats to check if this is the first correct answer
         const currentStats = JSON.parse(localStorage.getItem('userStats')) || { correctAnswers: 0 };
         const isFirstCorrectAnswer = currentStats.correctAnswers === 0;
         
         updateUserStats({
           totalQuestions: 1,
           correctAnswers: 1,
           rizzPointsChange: rizzPointsChange,
           difficulty: questionDifficulty,
           currentStreak: streak.current + 1,
           maxStreak: Math.max(streak.max, streak.current + 1),
           topic: topic,
           isCorrectAnswer: true, // Add flag to ensure this is processed as a correct answer
           forceCount: true, // Force this to be counted even if it might be a duplicate
           isFirstCorrectAnswer: isFirstCorrectAnswer, // Special flag for the very first correct answer
           questionId: pendingQuiz.question // Pass the full question for hashing
         });
       } else if (isSkipped) {
         // For skipped questions: break streak but don't count as wrong
         setStreak(prev => {
           const newStreak = { ...prev, current: 0 };
           
           // Save to localStorage
           localStorage.setItem('userStreak', JSON.stringify(newStreak));
           
           return newStreak;
         });
         
         // Update daily activity for skipped answers, but don't update main stats
         updateDailyActivity("skipped");
         
         // Don't call updateUserStats for skipped questions - we don't want to count them at all
         // Skip directly to the message display
       } else {
         // For incorrect answers
         setScore(prev => {
           const newWrong = prev.wrong + 1;
           return { ...prev, wrong: newWrong };
         });
         
         // Reset streak
         setStreak(prev => {
           const newStreak = { ...prev, current: 0 };
           
           // Save to localStorage
           localStorage.setItem('userStreak', JSON.stringify(newStreak));
           
           return newStreak;
         });
         
         // Update daily activity for incorrect answers
         updateDailyActivity("incorrect");
         
         // Update user stats with incorrect answer, rizz points, and reset streak
         
         // Get the current stats to check if this is the first incorrect answer
         const currentStats = JSON.parse(localStorage.getItem('userStats')) || { incorrectAnswers: 0 };
         const isFirstIncorrectAnswer = currentStats.incorrectAnswers === 0;
         
         updateUserStats({
           totalQuestions: 1, // Changed from score.correct + score.wrong + 1
           incorrectAnswers: 1,
           rizzPointsChange: rizzPointsChange,
           difficulty: questionDifficulty,
           currentStreak: 0,
           topic: topic,
           forceCount: true, // Force this to be counted even if it might be a duplicate
           isFirstIncorrectAnswer: isFirstIncorrectAnswer, // Special flag for the very first incorrect answer
           questionId: pendingQuiz.question // Pass the full question for hashing
         });
       }

       // Create static quiz display with difficulty
       const staticQuiz = renderStaticQuiz({ ...pendingQuiz, difficulty: questionDifficulty }, userAnswer);

       // Update topic distribution in user stats - only for non-skipped questions
       if (!isSkipped) {
         // Create a separate update for topic distribution with forceCount to ensure it's always processed
         updateUserStats({
           topicsDistribution: {
             [topic]: 1
           },
           // Include topic for proper rizz point tracking
           topic: topic,
           // Include rizzPointsChange to ensure topic-specific rizz is updated
           rizzPointsChange: rizzPointsChange,
           // Force count this update to ensure topic rizz is always updated
           forceCount: true,
           // Mark as auxiliary update (not a primary question count update)
           isAuxiliaryUpdate: true,
           // Do not include totalQuestions here to prevent double counting
           questionId: pendingQuiz.question // Pass the full question for hashing
         });
         
         // Log topic-specific rizz points for debugging
         setTimeout(() => {
           const currentStats = JSON.parse(localStorage.getItem('userStats')) || {};
           const topicRizz = currentStats.topicRizz || {};
           console.log(`Topic ${topic} now has ${topicRizz[topic] || 0} rizz points after update`);
           
           // Call debug function to log all topic rizz points
           debugTopicRizz();
         }, 500); // Small delay to ensure stats are updated
       }

       // Update response time statistics - only for non-skipped questions
       if (responseTime && !isSkipped) {
         updateUserStats({
           totalResponseTime: 1,
           averageResponseTime: responseTime,
           // Mark as auxiliary update (not a primary question count update)
           isAuxiliaryUpdate: true,
           // Do not include totalQuestions here to avoid double counting
           questionId: pendingQuiz.question // Pass the full question for hashing
         });
       }

       // Add to messages with difficulty info
       setMessages(prev => [...prev, { text: staticQuiz, sender: "quiz", type: "staticQuiz" }]);
       
       // Create the user answer message object
       let userAnswerMessage;
       if (isSkipped) {
         userAnswerMessage = { 
           text: "I skipped this question",
           sender: "user",
           answerDetails: {
             isSkipped: true,
             difficulty: questionDifficulty
           }
         };
       } else {
         userAnswerMessage = { 
           text: userAnswer, // Just the answer text, no "My answer is:" prefix
           sender: "user"
           // Removed answerDetails to hide incorrect status and rizz points
         };
       }
       
       // First add the user's answer message
       setMessages(prev => [...prev, userAnswerMessage]);
       
       // Then add explanation AFTER the user's answer if it exists
       if (pendingQuiz.explanation && pendingQuiz.explanation.length > 0) {
         // Create a more personalized explanation for incorrect answers
         let explanationText = pendingQuiz.explanation;
         
         // If the user answered incorrectly (not skipped), enhance the explanation
         if (!isSkipped && !isCorrect) {
           const userAnswerLetter = userAnswer.trim().toUpperCase();
           const correctAnswerLetter = pendingQuiz.correctAnswer.trim().toUpperCase();
           
           // Get the text of the user's chosen answer and the correct answer
           const userAnswerText = pendingQuiz.choices.find(choice => choice.label === userAnswerLetter)?.text || "";
           const correctAnswerText = pendingQuiz.choices.find(choice => choice.label === correctAnswerLetter)?.text || "";
           
           // Use the specific wrong answer explanation from OpenAI if available
           let specificExplanation = "";
           
           // Check if we have a specific explanation for this wrong answer
           if (pendingQuiz.wrongAnswerExplanations && 
               pendingQuiz.wrongAnswerExplanations[userAnswerLetter] && 
               pendingQuiz.wrongAnswerExplanations[userAnswerLetter].length > 0) {
             
             // Use the explanation provided by OpenAI for this specific wrong answer
             specificExplanation = pendingQuiz.wrongAnswerExplanations[userAnswerLetter];
             console.log(`Using OpenAI's explanation for why ${userAnswerLetter} is wrong:`, specificExplanation);
           } 
           // If no specific explanation is available from OpenAI, fall back to the existing logic
           else if (userAnswerText) {
             // Look for specific explanation about the user's wrong answer in the explanation
             const lowerCaseExplanation = explanationText.toLowerCase();
             const userAnswerPattern = new RegExp(`${userAnswerLetter}\\s+is\\s+wrong|${userAnswerLetter}\\s+confuses|option\\s+${userAnswerLetter}|${userAnswerLetter}\\)\\s+is|${userAnswerLetter}\\s+incorrectly`, 'i');
             
             // Extract the specific explanation for the user's wrong answer if it exists
             const match = explanationText.match(userAnswerPattern);
             
             if (match) {
               // Find the sentence containing the explanation for the wrong answer
               const startIndex = match.index;
               const endIndex = explanationText.indexOf('.', startIndex) + 1;
               if (endIndex > startIndex) {
                 specificExplanation = explanationText.substring(startIndex, endIndex).trim();
               }
             }
             
             // If we still couldn't find a specific explanation, create an engaging one
             if (!specificExplanation) {
               // Create a variety of engaging, meme-like explanations for wrong answers
               // Use an array of possible explanations for each scenario and pick one randomly
               
               // Helper function to get a random item from an array
               const getRandomItem = (array) => array[Math.floor(Math.random() * array.length)];
               
               // Arrays of engaging explanations for different scenarios
               const closeAnswerExplanations = [
                 `Ooof! ${userAnswerLetter} is like missing the basketball hoop by an inch - so close yet so far!`,
                 `Plot twist: ${userAnswerLetter} looks right until you spot what ${correctAnswerLetter} does better!`,
                 `${userAnswerLetter} is that "almost got it" moment we all know too well!`,
                 `${userAnswerLetter} is the "I thought I nailed it" answer that's just slightly off target!`,
                 `${userAnswerLetter} is like using Bing when Google had the answer all along!`
               ];
               
               const tooSimpleExplanations = [
                 `${userAnswerLetter} is the "I skipped reading the whole question" answer!`,
                 `${userAnswerLetter} is like bringing a knife to a gunfight - too basic for this challenge!`,
                 `Nice try, but ${userAnswerLetter} is the ELI5 version when we needed the PhD explanation!`,
                 `${userAnswerLetter} is what happens when you underestimate the question's complexity!`,
                 `${userAnswerLetter} is like using a calculator when the problem needs calculus!`
               ];
               
               const tooComplexExplanations = [
                 `${userAnswerLetter} is overthinking at its finest - sometimes the simple answer wins!`,
                 `${userAnswerLetter} is like writing a novel when "yes" would suffice!`,
                 `${userAnswerLetter} is what happens when you see patterns that aren't there!`,
                 `${userAnswerLetter} is the "I'm too smart for my own good" answer!`,
                 `${userAnswerLetter} is like bringing a supercomputer to solve 2+2!`
               ];
               
               const misconceptionExplanations = [
                 `${userAnswerLetter} is that classic trap that even experts fall into sometimes!`,
                 `${userAnswerLetter} is the answer your brain wants to pick before you think it through!`,
                 `${userAnswerLetter} is the "sounds right but isn't" option that tricks everyone!`,
                 `${userAnswerLetter} is like thinking Pluto is still a planet - outdated but understandable!`,
                 `${userAnswerLetter} is what happens when intuition overrules logic!`
               ];
               
               const genericExplanations = [
                 `${userAnswerLetter} looked tempting, but ${correctAnswerLetter} steals the show here!`,
                 `${userAnswerLetter} is like ordering vanilla when chocolate was clearly the better choice!`,
                 `${userAnswerLetter} is what happens when the question pulls a sneaky on you!`,
                 `${userAnswerLetter} is the red herring that almost got away with it!`,
                 `${userAnswerLetter} is the "seemed legit" option that wasn't quite right!`
               ];
               
               // Select the appropriate explanation based on the scenario
               if (userAnswerLetter === "A" && correctAnswerLetter === "B") {
                 specificExplanation = getRandomItem(misconceptionExplanations);
               } else if (userAnswerLetter === "A" && (correctAnswerLetter === "C" || correctAnswerLetter === "D")) {
                 specificExplanation = getRandomItem(tooSimpleExplanations);
               } else if (userAnswerLetter === "D" && (correctAnswerLetter === "A" || correctAnswerLetter === "B")) {
                 specificExplanation = getRandomItem(tooComplexExplanations);
               } else if (Math.abs(userAnswerLetter.charCodeAt(0) - correctAnswerLetter.charCodeAt(0)) === 1) {
                 specificExplanation = getRandomItem(closeAnswerExplanations);
               } else {
                 specificExplanation = getRandomItem(genericExplanations);
               }
             }
           }
           
           // Format the explanation with the concise explanation first, followed by the detailed explanation
           // Make sure there's a clear separation between the concise explanation and the detailed one
           const originalExplanation = explanationText.trim();
           
           // Only add the original explanation if it's not empty and doesn't start with the specific explanation
           // This prevents duplication if the specific explanation was extracted from the original
           if (specificExplanation && originalExplanation && !originalExplanation.startsWith(specificExplanation)) {
             explanationText = `<strong>${specificExplanation}</strong><br><br>${originalExplanation}`;
           } else if (specificExplanation) {
             explanationText = `<strong>${specificExplanation}</strong>`;
           }
         }
         
         setMessages(prev => [
           ...prev, 
           { 
             text: explanationText, 
             sender: "ai", 
             type: "explanation" 
           }
         ]);
       }

       // Save the question with detailed information for history
       const questionWithDetails = {
         ...pendingQuiz,
         userAnswer: isSkipped ? "SKIPPED" : userAnswer,
         isCorrect: isSkipped ? null : isCorrect, // Skipped questions are neither correct nor incorrect
         isSkipped: isSkipped,
         timestamp: Date.now(),
         responseTime: responseTime,
         topic: topic,
         difficulty: questionDifficulty,
         rizzPoints: rizzPointsChange,
         explanation: pendingQuiz.explanation || "" // Add explanation to question history
       };

       // Log the question details for debugging
       console.log("Saving question to history:", {
         question: pendingQuiz.question.substring(0, 30) + "...",
         isCorrect: questionWithDetails.isCorrect,
         isSkipped: questionWithDetails.isSkipped,
         userAnswer: questionWithDetails.userAnswer
       });

       // Store the question in our ref to add to history when the next question starts
       previousAnsweredQuestionRef.current = questionWithDetails;
       
       // Force history to update
       setHistoryUpdateFlag(prev => prev + 1);

       // Clear current quiz and show loading
       setCurrentQuiz(null);
       setIsLoading(true);

       // Small delay before fetching the next question to improve user experience
       setTimeout(async () => {
         try {
           const newQuizData = await fetchNewQuizQuestion();
           if (newQuizData) {
             console.log("Setting new quiz from separate fetch:", newQuizData.question);
             setCurrentQuiz(newQuizData);
             // Set consecutive questions to 1 for a new question after user answer
             setConsecutiveQuestions(1);
             console.log("Consecutive questions counter set to 1 (new question after answer)");
           } else {
             // If we couldn't get a new quiz, make sure we reset the loading state
             console.warn("Failed to fetch new quiz question");
           }
         } catch (error) {
           console.error("Error fetching new quiz:", error);
         } finally {
           // Ensure isLoading is set to false in all cases
           setIsLoading(false);
           setIsAnswering(false);
           isAnsweringRef.current = false;
         }
       }, 1000); // 1 second delay
     }
   } catch (error) {
     console.error('Error handling quiz answer:', error);
     setIsLoading(false);
     setIsAnswering(false);
     isAnsweringRef.current = false;
   }
 };

 // Helper function to extract keywords from text (could be moved elsewhere)
 function getKeywords(text) {
   if (!text) return [];

   const fillerWords = [
     "the", "a", "an", "and", "or", "but", "in", "on", "at", "to", "for", "with",
     "about", "of", "by", "is", "are", "was", "were", "be", "been", "being",
     "what", "which", "who", "whom", "whose", "when", "where", "why", "how"
   ];

   return text.toLowerCase()
     .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()?"']/g, "")
     .split(" ")
     .filter(word => word.length > 2 && !fillerWords.includes(word))
     .map(word => word.trim());
 }
  
  // Debug function to log topic-specific rizz points
  const debugTopicRizz = () => {
    const currentStats = JSON.parse(localStorage.getItem('userStats')) || {};
    const topicRizz = currentStats.topicRizz || {};
    console.log("=== TOPIC RIZZ DEBUG ===");
    console.log("All topic rizz data:", topicRizz);
    Object.entries(topicRizz).forEach(([topic, rizz]) => {
      console.log(`Topic "${topic}": ${rizz} rizz points`);
    });
    console.log("======================");
  };
  
  // Function to reset topic-specific rizz points (for testing)
  const resetTopicRizz = () => {
    if (window.confirm("Are you sure you want to reset all topic-specific rizz points? This will reset your topic mastery levels.")) {
      const currentStats = JSON.parse(localStorage.getItem('userStats')) || {};
      currentStats.topicRizz = {};
      localStorage.setItem('userStats', JSON.stringify(currentStats));
      console.log("All topic-specific rizz points have been reset.");
      alert("Topic mastery levels have been reset. Please refresh the page to see the changes.");
    }
  };
  
  // Function to clear history
  const handleClearHistory = () => {
    if (window.confirm("Are you sure you want to clear all question history?")) {
      setAskedQuestions([]);
      localStorage.removeItem('techGuyGregAskedQuestions');
    }
  };

  // Toggle debug mode (only for development)
  const toggleDebug = () => {
    if (debugMode) {
      setDebugMode(false);
    } else {
      // Double verification to prevent accidental activation
      if (window.confirm("Enable debug mode?")) {
        setDebugMode(true);
      }
    }
  };

  // Function to determine question difficulty based on complexity - optimized version
  const determineQuestionDifficulty = (question) => {
    if (!question || !question.question) return 'medium'; // Default to medium if no question

    // Simple heuristics instead of expensive calculations
    const questionText = question.question;
    const choices = question.choices || [];
    
    // Simplified complexity keywords - reduced list to most important indicators
    const complexityKeywords = [
      'algorithm', 'complexity', 'optimize', 'advanced',
      'recursion', 'dynamic programming', 'tree', 'graph', 
      'time complexity', 'space complexity'
    ];
    
    // Basic check instead of expensive iteration
    const questionText_lower = questionText.toLowerCase();
    let difficultyScore = 0;
    
    // Factor 1: Question length (longer = harder)
    difficultyScore += Math.min(30, (questionText.length / 20));
    
    // Factor 2: Keyword check (simplified to reduce processing)
    for (let i = 0; i < complexityKeywords.length; i++) {
      if (questionText_lower.includes(complexityKeywords[i])) {
        difficultyScore += 10;
        // Break early if we have enough evidence of difficulty
        if (difficultyScore >= 70) break;
      }
    }
    
    // Determine difficulty category
    if (difficultyScore >= 70) return 'hard';
    if (difficultyScore >= 35) return 'medium';
    return 'easy';
  };
  
  // Function to get rizz points based on question difficulty - with 10x multiplier
  const getRizzPointsForDifficulty = (difficulty, isCorrect) => {
    // Base points for each difficulty level
    switch(difficulty) {
      case 'hard':
        return isCorrect ? 300 : -200;  // Was 250/-150
      case 'medium':
        return isCorrect ? 200 : -125;  // Was 150/-100
      case 'easy':
      default:
        return isCorrect ? 100 : -75;   // Was 100/-50
    }
  };

  // Sync streak data across different localStorage keys on component mount
  useEffect(() => {
    try {
      // Get streak data from all sources
      const userStatsStreak = JSON.parse(localStorage.getItem('userStats') || '{}');
      const techGuyGregStreak = JSON.parse(localStorage.getItem('techGuyGregStreak') || '{"current":0,"max":0}');
      const userStreak = JSON.parse(localStorage.getItem('userStreak') || '{"current":0,"max":0}');
      
      // Find the maximum values
      const maxCurrentStreak = Math.max(
        userStatsStreak.currentStreak || 0,
        techGuyGregStreak.current || 0,
        userStreak.current || 0
      );
      
      const maxMaxStreak = Math.max(
        userStatsStreak.maxStreak || 0,
        techGuyGregStreak.max || 0,
        userStreak.max || 0
      );
      
      // Create the synced streak object
      const syncedStreak = {
        current: maxCurrentStreak,
        max: maxMaxStreak
      };
      
      // Update all localStorage items
      localStorage.setItem('userStreak', JSON.stringify(syncedStreak));
      localStorage.setItem('techGuyGregStreak', JSON.stringify(syncedStreak));
      
      // Update userStats if it exists
      if (localStorage.getItem('userStats')) {
        const userStats = JSON.parse(localStorage.getItem('userStats'));
        userStats.currentStreak = syncedStreak.current;
        userStats.maxStreak = syncedStreak.max;
        localStorage.setItem('userStats', JSON.stringify(userStats));
      }
      
      // Update the streak state
      setStreak(syncedStreak);
      
      console.log('Streak data synced:', syncedStreak);
    } catch (error) {
      console.error('Error syncing streak data:', error);
    }
  }, []);

  // Save topic coverage to localStorage when it changes
  useEffect(() => {
    if (Object.keys(topicCoverage).length > 0) {
      localStorage.setItem('techGuyGregTopicCoverage', JSON.stringify(topicCoverage));
    }
  }, [topicCoverage]);

  // Function to update topic coverage
  const updateTopicCoverage = (topic, subtopic) => {
    // Normalize topic name to handle any changes in capitalization or spacing
    const normalizedTopic = topic.trim();
    const normalizedSubtopic = subtopic ? subtopic.trim() : null;
    
    console.log(`Updating topic coverage for ${normalizedTopic}${normalizedSubtopic ? ` / ${normalizedSubtopic}` : ''}`);
    
    setTopicCoverage(prevCoverage => {
      const newCoverage = { ...prevCoverage };
      
      if (!newCoverage[normalizedTopic]) {
        newCoverage[normalizedTopic] = { 
          count: 0,
          subtopics: {}
        };
      }
      
      newCoverage[normalizedTopic].count = (newCoverage[normalizedTopic].count || 0) + 1;
      
      if (normalizedSubtopic) {
        if (!newCoverage[normalizedTopic].subtopics) {
          newCoverage[normalizedTopic].subtopics = {};
        }
        newCoverage[normalizedTopic].subtopics[normalizedSubtopic] = (newCoverage[normalizedTopic].subtopics[normalizedSubtopic] || 0) + 1;
      }
      
      return newCoverage;
    });
  };

  // Function to migrate old topic names to new ones
  const migrateTopicNames = () => {
    const currentStats = JSON.parse(localStorage.getItem('userStats')) || {};
    
    // Skip if no stats or no topic data
    if (!currentStats || !currentStats.topicsDistribution) return;
    
    // Define mapping of old topic names to new ones
    const topicMapping = {
      "JavaScript": "CS Fundamentals",
      "React": "CS Fundamentals",
      "Python": "CS Fundamentals",
      "LeetCode Question": "LeetCode"
    };
    
    let migrationPerformed = false;
    
    // Migrate topicsDistribution
    if (currentStats.topicsDistribution) {
      const newTopicsDistribution = {};
      
      Object.entries(currentStats.topicsDistribution).forEach(([oldTopic, count]) => {
        const newTopic = topicMapping[oldTopic] || oldTopic;
        newTopicsDistribution[newTopic] = (newTopicsDistribution[newTopic] || 0) + count;
        
        if (newTopic !== oldTopic) {
          console.log(`Migrated topic distribution: ${oldTopic} -> ${newTopic}`);
          migrationPerformed = true;
        }
      });
      
      currentStats.topicsDistribution = newTopicsDistribution;
    }
    
    // Migrate topicRizz
    if (currentStats.topicRizz) {
      const newTopicRizz = {};
      
      Object.entries(currentStats.topicRizz).forEach(([oldTopic, rizz]) => {
        const newTopic = topicMapping[oldTopic] || oldTopic;
        newTopicRizz[newTopic] = (newTopicRizz[newTopic] || 0) + rizz;
        
        if (newTopic !== oldTopic) {
          console.log(`Migrated topic rizz: ${oldTopic} -> ${newTopic}`);
          migrationPerformed = true;
        }
      });
      
      currentStats.topicRizz = newTopicRizz;
    }
    
    // Save if any migrations were performed
    if (migrationPerformed) {
      console.log("Topic migration completed. Saving updated stats.");
      localStorage.setItem('userStats', JSON.stringify(currentStats));
    }
  };
  
  // Call the migration function on component mount
  useEffect(() => {
    migrateTopicNames();
  }, []);

  // State for rizz points particles
  const [showRizzParticles, setShowRizzParticles] = useState(false);
  const [particleType, setParticleType] = useState('positive');

  // Function to show rizz particles
  const showParticles = (type) => {
    setParticleType(type);
    setShowRizzParticles(true);
    setTimeout(() => {
      setShowRizzParticles(false);
    }, 1500);
  };

  return (
    <ChatContext.Provider value={{ 
      topicCoverage, 
      updateTopicCoverage,
      topic,
      rizzPoints 
    }}>
    <div className="chat-container">
      <div className="chat-header">
          <span className="header-title">💬 TechGuyGreg Exam Prep Pro</span>
        <div className="header-buttons">
            {currentQuiz && (
              <div className="header-timer">
                <span className="timer-icon">⏱️</span>
                <span className={`timer-value ${timeLeft < 10 ? 'timer-low' : ''}`}>{timeLeft}s</span>
              </div>
            )}
          <button
            className="stats-button"
            onClick={() => setShowStats(true)}
            title="View Your Stats"
          >
            <span className="stats-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 20V10"></path>
                <path d="M12 20V4"></path>
                <path d="M6 20v-6"></path>
              </svg>
            </span>
            <span className="stats-label">My Stats</span>
          </button>
          <button
            className="history-button"
            onClick={() => setShowHistory(true)}
            title="View Question History"
          >
            <span className="history-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 8v4l3 3"></path>
                <path d="M3.05 11a9 9 0 1 1 .5 4"></path>
                <path d="M3 14V8h6"></path>
              </svg>
            </span>
            <span className="history-label">Question History</span>
          </button>
          {debugMode && (
            <button
              className="debug-button"
              onClick={toggleDebug}
              title="Disable Debug Mode"
            >
              🛑
            </button>
          )}
        </div>
      </div>

        <div className="topic-selector">
          <div className="topic-selector-label">Select Quiz Topic:</div>
          <select
            className="topic-dropdown"
            value={topic}
            onChange={e => {
              // If there's a current quiz, cancel it before changing topics
              if (currentQuiz) {
                setCurrentQuiz(null);
                // Add a message to inform the user that the quiz was cancelled
                setMessages(prevMessages => [
                  ...prevMessages,
                  {
                    type: "system",
                    text: `Quiz cancelled. Topic changed to ${e.target.value}.`,
                    timestamp: new Date().toISOString(),
                  }
                ]);
              }
              setTopic(e.target.value);
            }}
            disabled={isLoading}
          >
            <option value="LeetCode">LeetCode</option>
            <option value="DS&A">DS&A</option>
            <option value="OOP">OOP</option>
            <option value="Operating Systems">Operating Systems</option>
            <option value="Databases">Databases</option>
            <option value="CS Fundamentals">CS Fundamentals</option>
            <option value="System Design">System Design</option>
            <option value="Networking">Networking</option>
            <option value="Finance Resource Management">Finance Resource Management</option>
            <option value="Pediatric Nursing">Pediatric Nursing</option>
            <option value="Maternity and Newborn Nursing">Maternity & Newborn Nursing</option>
          </select>
        </div>

        <div className="controls-container">
          <div className="controls-top">
          <div className="controls-right">
            <button
              className="quiz-button"
              onClick={handleStartQuiz}
              disabled={isLoading}
            >
              Start Quiz
            </button>

            <TimerOptions
              selectedTimer={timerDuration}
              onSelectTimer={handleTimerSelect}
            />
          </div>
        </div>

        <div className="score-row">
          <div className="score-display">
            <span className="score-streak" title="Current Streak">Streak: {streak.current}</span>
            <span className="score-streak-max" title="Best Streak">Best: {streak.max}</span>
            <span className={`score-rizz ${rizzAnimating ? 'animating' : ''}`} title="Rizz Points">
              Rizz: {rizzPoints.toLocaleString()}
              {rizzPointsChange !== 0 && (
                <span className={`rizz-points-change ${rizzPointsChange > 0 ? 'positive' : 'negative'}`}>
                  {rizzPointsChange > 0 ? '+' : '-'}{Math.abs(rizzPointsChange).toLocaleString()}
                </span>
              )}
              {showRizzParticles && (
                <div className={`rizz-particles ${particleType}`}>
                  {[...Array(12)].map((_, i) => (
                    <div key={i} className="particle"></div>
                  ))}
                </div>
              )}
            </span>
          </div>
            {/* Timer display moved to header */}
        </div>
      </div>

      <div className="chat-messages">
        {messages.map((msg, index) => {
          if (debugMode && msg.debugInfo) {
            return <DebugMessage key={`debug-${index}`} text={msg.debugInfo} />;
          }

          if (msg.type === "explanation") {
            // Format any emojis that might be in the explanation
            const formattedText = msg.text.replace(/:([\w-]+):/g, (match, p1) => {
              // Convert emoji shortcodes to actual emojis if needed
              return match;
            });
            
            // Create a safe HTML representation with styled elements
            const createMarkup = () => {
              return { __html: formattedText };
            };
            
            return (
              <div 
                key={index} 
                className="message explanation" 
                dangerouslySetInnerHTML={createMarkup()} 
              />
            );
          }

          if (msg.type === "quiz-help") {
            return (
              <div key={index} className="message quiz-help">
                {msg.text}
              </div>
            );
          }

          if (msg.type === "staticQuiz") {
            return (
              <div
                key={index}
                className="message static-quiz"
                dangerouslySetInnerHTML={{ __html: msg.text }}
              />
            );
          }

          return (
            <div
              key={index}
              className={`message ${msg.sender}`}
            >
              {msg.answerDetails ? (
                <>
                  <div dangerouslySetInnerHTML={{ __html: msg.text.replace(/\n/g, "<br>") }} />
                  <div className="answer-details">
                    <span className={`answer-status ${msg.answerDetails.isCorrect ? 'correct' : 'incorrect'}`}>
                      {msg.answerDetails.isCorrect ? 'Correct' : 'Incorrect'}
                    </span>
                    <div className="answer-metadata">
                      <span className={`difficulty-badge ${msg.answerDetails.difficulty}`}>
                        {msg.answerDetails.difficulty.toUpperCase()}
                      </span>
                      <span className={`rizz-change ${msg.answerDetails.rizzPointsChange > 0 ? 'positive' : 'negative'}`}>
                        {msg.answerDetails.rizzPointsChange > 0 ? '+' : ''}{msg.answerDetails.rizzPointsChange.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <div dangerouslySetInnerHTML={{ __html: msg.text.replace(/\n/g, "<br>") }} />
              )}
            </div>
          );
        })}

        {isLoading && <LoadingIndicator />}

        {currentQuiz && (
          <QuizQuestion
            key={currentQuiz.question}
            quizData={currentQuiz}
            onAnswer={handleQuizAnswer}
            isLoading={isLoading}
          />
        )}

        <div ref={chatRef} />
      </div>

      <div className="chat-input-container">
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && !isLoading && handleSend()}
          onFocus={() => {
            // Prevent scrolling when input is focused on mobile
            if (window.innerWidth <= 768) {
              document.body.classList.add('input-focused');
            }
          }}
          onBlur={() => {
            // Remove class when input loses focus
            document.body.classList.remove('input-focused');
          }}
          placeholder={currentQuiz
            ? "Ask for help or hints about this question..."
            : "Ask for help or explanations on CS topics..."}
          className="chat-input"
          disabled={isLoading}
        />
        <button
          onClick={handleSend}
          className="send-button"
          disabled={isLoading || !input.trim()}
        >
          Send
        </button>
      </div>

      {showHistory && (
        <QuestionHistory
          askedQuestions={askedQuestions}
          onClearHistory={handleClearHistory}
          onClose={() => setShowHistory(false)}
          updateFlag={historyUpdateFlag} // Pass the update flag to force rerenders
        />
      )}

      {showStats && (
        <UserStats onClose={() => setShowStats(false)} />
      )}

      {/* Add diagnostic tool in debug mode */}
      {debugMode && (
        <QuizDiagnostics
          askedQuestions={askedQuestions}
          isDuplicateQuizQuestion={isDuplicateQuizQuestion}
        />
      )}

      {/* Hidden debug toggle (triple-click in bottom corner) */}
      <div
        className="debug-toggle"
        style={{ position: 'absolute', bottom: 0, right: 0, width: '20px', height: '20px', opacity: 0 }}
        onClick={(e) => {
          if (e.detail === 3) toggleDebug();
        }}
      />
        
        {/* Add the StatsDebugger in development mode */}
        {process.env.NODE_ENV === 'development' && <StatsDebugger />}
        {/* Don't show the debugger in production */}
    </div>
    </ChatContext.Provider>
  );
}

export default ChatBox;